import { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { formatNumberWithCommas } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Loader2, LogOut, Trash2, Sparkles, Send, KeyRound, CheckCircle2, XCircle, AlertTriangle, Clock, Eye, Play, FileText, RefreshCw, Download, Link as LinkIcon, Copy, ExternalLink, Beaker, Shield, Facebook, User, ClipboardList, Mail, Target, Package, StickyNote, Edit2, Globe } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { AuditProgressIndicator } from "@/components/AuditProgressIndicator";
import { AuditResultsSummary } from "@/components/AuditResultsSummary";
import { AuditProgressDialog } from "@/components/AuditProgressDialog";
import { Input } from "@/components/ui/input";
import { logger } from "@/lib/logger";
import { Label } from "@/components/ui/label";
import { z } from "zod";
import { generateQuizReportPDF } from "@/lib/pdfGenerator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { questions } from "@/data/questions";
import { interstitials } from "@/data/interstitials";
import { SocialSellingAuditProcess } from "@/components/SocialSellingAuditProcess";
import { useIsMobile } from "@/hooks/use-mobile";
import { MobileLeadCard } from "@/components/MobileLeadCard";
import { MobileQuizCard } from "@/components/MobileQuizCard";
import { MobileSocialSellingCard } from "@/components/MobileSocialSellingCard";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { HeadlineVariantsManager } from "@/components/admin/HeadlineVariantsManager";
import { AdminReportPreview } from "@/components/admin/AdminReportPreview";
import { AdminScoringDetails } from "@/components/admin/AdminScoringDetails";
import { QuizEnrichedDataViewer } from "@/components/admin/QuizEnrichedDataViewer";
import { LeadEnrichedDataViewer } from "@/components/admin/LeadEnrichedDataViewer";
import { TestingPanel } from "@/components/admin/TestingPanel";
import { AutomatedTestingPanel } from "@/components/admin/AutomatedTestingPanel";
import { ExcludedDomainsManager } from "@/components/admin/ExcludedDomainsManager";
import { TestAnalyticsDashboard } from "@/components/admin/TestAnalyticsDashboard";
import { PageleadsIntegrationDocs } from "@/components/admin/PageleadsIntegrationDocs";
import { SimpleLandingCMS } from "@/components/admin/SimpleLandingCMS";
import { Switch } from "@/components/ui/switch";

interface LeadSubmission {
  id: string;
  name: string;
  email: string;
  phone: string;
  company: string | null;
  quiz_score: number | null;
  quiz_band: string | null;
  quiz_answers: any | null;
  created_at: string;
  enriched_data: any | null;
  enriched_at: string | null;
  pushed_to_crm_at: string | null;
  position: string | null;
  status: string | null;
  crm_status: string | null;
  report_token: string | null;
  report_password: string | null;
  quiz_submission_id: string | null;
  submission_type: string | null;
  slug: string | null;
  notes: string | null;
  source_project: string | null;
}


interface QuizSubmission {
  id: string;
  created_at: string;
  quiz_score: number | null;
  quiz_band: string | null;
  quiz_answers: any | null;
  domain_input: string | null;
  competitor_analysis: any | null;
  selected_competitors: string[] | null;
  converted_to_lead_id: string | null;
  report_download_email: string | null;
  report_downloaded_at: string | null;
  ai_analysis: any | null;
  report_token: string | null;
  report_password: string | null;
  enriched_data: any | null;
  enriched_at: string | null;
  pushed_to_crm_at: string | null;
  session_id: string | null;
  lead_submission?: LeadSubmission | null;
  slug: string | null;
  notes: string | null;
  status: string | null;
}

interface SocialSellingSubmission {
  id: string;
  user_domain: string;
  competitor_domains: string[] | null;
  email: string;
  phone: string;
  status: string;
  created_at: string;
  updated_at: string;
  report_data: any | null;
}

const Admin = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedTab, setSelectedTab] = useState("submissions");
  const [submissions, setSubmissions] = useState<LeadSubmission[]>([]);
  const [quizSubmissions, setQuizSubmissions] = useState<QuizSubmission[]>([]);
  const [socialSellingSubmissions, setSocialSellingSubmissions] = useState<SocialSellingSubmission[]>([]);
  const [selectedQuizSubmission, setSelectedQuizSubmission] = useState<QuizSubmission | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [selectedSubmissions, setSelectedSubmissions] = useState<Set<string>>(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const toggleSelectSubmission = (key: string) => {
    setSelectedSubmissions(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const deleteQuizGroup = async (quizId: string) => {
    const quiz = quizSubmissions.find(item => item.id === quizId);
    const query = supabase.from('quiz_submissions').delete();

    if (quiz?.session_id && quiz?.domain_input) {
      return query.eq('session_id', quiz.session_id).eq('domain_input', quiz.domain_input);
    }

    return query.eq('id', quizId);
  };

  const handleBulkDelete = async () => {
    try {
      const items = Array.from(selectedSubmissions);
      const byType: Record<string, string[]> = { quiz: [], lead: [], ssa: [] };
      items.forEach(s => {
        const idx = s.indexOf(':');
        const t = s.slice(0, idx);
        const id = s.slice(idx + 1);
        if (byType[t]) byType[t].push(id);
      });
      const ops: Promise<any>[] = [];
      byType.quiz.forEach(id => ops.push(Promise.resolve(deleteQuizGroup(id))));
      if (byType.lead.length) ops.push(Promise.resolve(supabase.from('lead_submissions').delete().in('id', byType.lead)));
      if (byType.ssa.length) ops.push(Promise.resolve(supabase.from('social_selling_submissions').delete().in('id', byType.ssa)));
      const results = await Promise.all(ops);
      const errs = results.filter((r: any) => r?.error);
      if (errs.length) {
        toast({ title: 'Błąd', description: errs[0].error?.message || 'Nie udało się usunąć niektórych rekordów', variant: 'destructive' });
      } else {
        toast({ title: 'Sukces', description: `Usunięto ${items.length} zaznaczonych wpisów wraz z ich ukrytymi duplikatami` });
      }
      setSelectedSubmissions(new Set());
      setBulkDeleteOpen(false);
      fetchSubmissions();
      fetchQuizSubmissions();
      fetchSocialSellingSubmissions();
    } catch (error: any) {
      toast({ title: 'Błąd', description: error.message || 'Nie udało się usunąć rekordów', variant: 'destructive' });
    }
  };
  const [enrichingId, setEnrichingId] = useState<string | null>(null);
  const [pushingToSalesflareId, setPushingToSalesflareId] = useState<string | null>(null);
  const [selectedSubmission, setSelectedSubmission] = useState<LeadSubmission | null>(null);
  const [showEnrichDialog, setShowEnrichDialog] = useState(false);
  const [showLeadEnrichedDialog, setShowLeadEnrichedDialog] = useState(false);
  const [showQuizDialog, setShowQuizDialog] = useState(false);
  const [showQuizEnrichedDialog, setShowQuizEnrichedDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  const [showTestData, setShowTestData] = useState(false);
  const [sourceProjectFilter, setSourceProjectFilter] = useState<string>('all');
  const [showCompetitorTestDialog, setShowCompetitorTestDialog] = useState(false);
  const [testDomain, setTestDomain] = useState("");
  const [testingCompetitors, setTestingCompetitors] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);
  const [testStage, setTestStage] = useState<'analyzing' | 'searching' | 'verifying' | 'complete'>('analyzing');
  const [testProgress, setTestProgress] = useState(0);
  const [forceRefresh, setForceRefresh] = useState(false);
  const [longTimeouts, setLongTimeouts] = useState(false);
  const [enableStep3LLM, setEnableStep3LLM] = useState(false);
  const [activeAuditId, setActiveAuditId] = useState<string | null>(null);
  const [showApiCheckDialog, setShowApiCheckDialog] = useState(false);
  const [apiCheckResults, setApiCheckResults] = useState<any>(null);
  const [testingApis, setTestingApis] = useState(false);
  const [progressDialogOpen, setProgressDialogOpen] = useState(false);
  const [selectedAuditId, setSelectedAuditId] = useState<string | null>(null);
  const [useMockEnrichB2B, setUseMockEnrichB2B] = useState(false);
  const [testCompanyUrl, setTestCompanyUrl] = useState("");
  const [testingCompanyUrl, setTestingCompanyUrl] = useState(false);
  const [companyTestResult, setCompanyTestResult] = useState<any>(null);
  const [searchName, setSearchName] = useState("");
  const [companyNames, setCompanyNames] = useState("");
  const [salesflareTestResult, setSalesflareTestResult] = useState<any>(null);
  const [showSalesflareDialog, setShowSalesflareDialog] = useState(false);
  const [excludedDomainsModalOpen, setExcludedDomainsModalOpen] = useState(false);
  
  // Notes modal state
  const [notesModal, setNotesModal] = useState<{
    isOpen: boolean;
    type: 'quiz' | 'lead' | null;
    id: string | null;
    notes: string;
  }>({ isOpen: false, type: null, id: null, notes: '' });
  
  // LinkedIn Searcher state
  const [searchDomains, setSearchDomains] = useState<string[]>(['']);
  const [searchResults, setSearchResults] = useState<Array<{
    domain: string;
    linkedinUrl: string | null;
    score: number;
    title: string | null;
    status: string;
  }>>([]);
  const [searchingLinkedIn, setSearchingLinkedIn] = useState(false);
  
  // Competition Search state
  const [competitionDomain, setCompetitionDomain] = useState<string>('');
  const [competitionResults, setCompetitionResults] = useState<{
    success: boolean;
    confidence: string;
    competitors: Array<{
      name: string;
      domain: string;
      score: number;
      competition_level: string;
    }>;
    company_name: string;
    industry: string;
    metadata: any;
  } | null>(null);
  const [searchingCompetition, setSearchingCompetition] = useState(false);
  const [fastMode, setFastMode] = useState(true);
  
  // LinkedIn search for competitors
  const [competitorLinkedInUrls, setCompetitorLinkedInUrls] = useState<Record<string, {
    url: string | null;
    status: 'loading' | 'found' | 'not_found' | 'error';
    title?: string;
  }>>({});
  
  // Competition Queue state
  const [competitionQueue, setCompetitionQueue] = useState<Array<{
    id: string;
    domain: string;
    fast_mode: boolean;
    status: string;
    progress: string | null;
    progress_percent: number;
    result_data: any;
    error_message: string | null;
    created_at: string;
    started_at: string | null;
    completed_at: string | null;
  }>>([]);
  const [queuePolling, setQueuePolling] = useState(false);
  
  // Bright Data test state
  const [testLinkedInUrl, setTestLinkedInUrl] = useState('');
  const [testingBrightData, setTestingBrightData] = useState(false);
  const [brightDataResult, setBrightDataResult] = useState<any>(null);
  
  // Enrichment queue state
  const [enrichmentQueue, setEnrichmentQueue] = useState<Array<{
    leadId: string;
    manualDomain?: string | null;
    manualKrs?: string | null;
  }>>([]);
  const [currentlyEnriching, setCurrentlyEnriching] = useState<string | null>(null);
  const [enrichmentProgress, setEnrichmentProgress] = useState<Record<string, {
    status: 'queued' | 'processing' | 'completed' | 'error';
    startedAt?: number;
    completedAt?: number;
    error?: string;
  }>>({});
  
  // Enrichment modal state
  const [enrichmentModal, setEnrichmentModal] = useState<{
    isOpen: boolean;
    submission: LeadSubmission | null;
    manualDomain: string;
    manualKrs: string;
  }>({ isOpen: false, submission: null, manualDomain: '', manualKrs: '' });
  
  // Bizraport API test state
  const [testKrs, setTestKrs] = useState('');
  const [testingBizraport, setTestingBizraport] = useState(false);
  const [bizraportTestResult, setBizraportTestResult] = useState<any>(null);
  const [showTestingTools, setShowTestingTools] = useState(false);
  
  // Report view counts state
  const [reportViewCounts, setReportViewCounts] = useState<Record<string, number>>({});
  
  // Refs for synchronized scrolling
  const topScrollRef = useRef<HTMLDivElement>(null);
  const tableScrollRef = useRef<HTMLDivElement>(null);

  // Unified sorted list of all submissions (quizzes + leads + SSA)
  const unifiedSubmissions = useMemo(() => {
    const quizItems = quizSubmissions.map(quiz => ({
      type: 'quiz' as const,
      id: quiz.id,
      created_at: quiz.created_at,
      data: quiz
    }));
    const leadItems = submissions.map(lead => ({
      type: 'lead' as const,
      id: lead.id,
      created_at: lead.created_at,
      data: lead
    }));
    const ssaItems = socialSellingSubmissions.map(ssa => ({
      type: 'ssa' as const,
      id: ssa.id,
      created_at: ssa.created_at,
      data: ssa
    }));
    return [...quizItems, ...leadItems, ...ssaItems].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }, [quizSubmissions, submissions, socialSellingSubmissions]);

  useEffect(() => {
    checkAdminAccess();
  }, []);
  
  useEffect(() => {
    if (isAdmin) {
      fetchSubmissions();
      fetchQuizSubmissions();
      fetchSocialSellingSubmissions();
    }
  }, [showTestData, sourceProjectFilter, isAdmin]);
  
  // Real-time subscription for new quiz submissions
  useEffect(() => {
    if (!isAdmin) return;

    console.log('🔴 Setting up realtime subscription for quiz_submissions');

            const channel = supabase
      .channel('quiz-submissions-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'quiz_submissions'
        },
        (payload) => {
          console.log('🔴 NEW QUIZ SUBMISSION:', payload);
          // Add new quiz to the list (all quizzes now)
          setQuizSubmissions(prev => {
            // Check if quiz already exists to prevent duplicates
            const exists = prev.some(q => q.id === payload.new.id);
            if (exists) {
              console.log('⚠️ Quiz already exists, skipping duplicate');
              return prev;
            }
            
            toast({
              title: "Nowy quiz!",
              description: `Quiz został ukończony (${payload.new.quiz_score}/10)`,
            });
            return [payload.new as QuizSubmission, ...prev];
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'quiz_submissions'
        },
        (payload) => {
          console.log('🔴 QUIZ UPDATE:', payload);
          // Update existing quiz if it exists (keep all quizzes in list now)
          setQuizSubmissions(prev => {
            const index = prev.findIndex(q => q.id === payload.new.id);
            if (index >= 0) {
              const updated = [...prev];
              updated[index] = payload.new as QuizSubmission;
              return updated;
            }
            return prev;
          });
        }
      )
      .subscribe();

    return () => {
      console.log('🔴 Cleaning up realtime subscription');
      supabase.removeChannel(channel);
    };
  }, [isAdmin]);

  // Real-time subscription for new lead submissions
  useEffect(() => {
    if (!isAdmin) return;

    console.log('🔴 Setting up realtime subscription for lead_submissions');

    const leadChannel = supabase
      .channel('lead-submissions-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'lead_submissions'
        },
        (payload) => {
          console.log('🔴 NEW LEAD SUBMISSION:', payload);
          
          // Only add lead submissions that are NOT linked to quiz submissions
          if (payload.new.quiz_submission_id) {
            console.log('⚠️ Lead is linked to quiz, skipping (will be shown in quiz row)');
            return;
          }
          
          // Only add if test data visibility matches
          const isTestData = payload.new.is_test_data || false;
          const shouldShow = showTestData || !isTestData;
          
          if (shouldShow) {
            setSubmissions(prev => {
              // Check if lead already exists to prevent duplicates
              const exists = prev.some(l => l.id === payload.new.id);
              if (exists) {
                console.log('⚠️ Lead already exists, skipping duplicate');
                return prev;
              }
              
              toast({
                title: isTestData ? "Nowy test lead!" : "Nowy lead!",
                description: `Lead został dodany: ${payload.new.email}`,
              });
              return [payload.new as LeadSubmission, ...prev];
            });
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'lead_submissions'
        },
        (payload) => {
          console.log('🔴 LEAD UPDATE:', payload);
          // Update existing lead if it exists
          setSubmissions(prev => {
            const index = prev.findIndex(l => l.id === payload.new.id);
            if (index >= 0) {
              const updated = [...prev];
              updated[index] = payload.new as LeadSubmission;
              return updated;
            }
            return prev;
          });
        }
      )
      .subscribe();

    return () => {
      console.log('🔴 Cleaning up lead submissions realtime subscription');
      supabase.removeChannel(leadChannel);
    };
  }, [isAdmin, showTestData]);

  // Real-time subscription for new social selling submissions
  useEffect(() => {
    if (!isAdmin) return;

    console.log('🔴 Setting up realtime subscription for social_selling_submissions');

    const ssaChannel = supabase
      .channel('social-selling-submissions-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'social_selling_submissions'
        },
        (payload) => {
          console.log('🔴 NEW SSA SUBMISSION:', payload);
          setSocialSellingSubmissions(prev => {
            const exists = prev.some(s => s.id === payload.new.id);
            if (exists) return prev;
            
            toast({
              title: "Nowy Social Selling Audit!",
              description: `Zgłoszenie dla: ${(payload.new as SocialSellingSubmission).user_domain}`,
            });
            return [payload.new as SocialSellingSubmission, ...prev];
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'social_selling_submissions'
        },
        (payload) => {
          console.log('🔴 SSA UPDATE:', payload);
          setSocialSellingSubmissions(prev => {
            const index = prev.findIndex(s => s.id === payload.new.id);
            if (index >= 0) {
              const updated = [...prev];
              updated[index] = payload.new as SocialSellingSubmission;
              return updated;
            }
            return prev;
          });
        }
      )
      .subscribe();

    return () => {
      console.log('🔴 Cleaning up SSA realtime subscription');
      supabase.removeChannel(ssaChannel);
    };
  }, [isAdmin]);
  
  // Poll competition queue every 3 seconds when there are active jobs
  useEffect(() => {
    const activeJobs = competitionQueue.filter(
      j => j.status === 'queued' || j.status === 'processing'
    );

    if (activeJobs.length === 0) {
      setQueuePolling(false);
      return;
    }

    setQueuePolling(true);
    const interval = setInterval(() => {
      fetchCompetitionQueue();
    }, 3000);

    return () => clearInterval(interval);
  }, [competitionQueue]);

  // Poll active audits every 5 seconds
  useEffect(() => {
    const processingAudits = socialSellingSubmissions.filter(
      s => s.status === 'processing'
    );

    if (processingAudits.length === 0) return;

    const interval = setInterval(() => {
      fetchSocialSellingSubmissions();
    }, 5000);

    return () => clearInterval(interval);
  }, [socialSellingSubmissions]);

  // Process enrichment queue automatically
  useEffect(() => {
    if (enrichmentQueue.length > 0 && !currentlyEnriching) {
      processEnrichmentQueue();
    }
  }, [enrichmentQueue.length, currentlyEnriching]);

  const checkAdminAccess = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      navigate('/auth');
      return;
    }

    // Check if user has admin role
    const { data: roles } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', session.user.id)
      .eq('role', 'admin')
      .single();

    if (!roles) {
      toast({
        title: "Brak dostępu",
        description: "Nie masz uprawnień administratora",
        variant: "destructive"
      });
      navigate('/');
      return;
    }

    setIsAdmin(true);
    fetchSubmissions();
    fetchQuizSubmissions();
    fetchSocialSellingSubmissions();
    fetchCompetitionQueue();
  };
  
  const fetchCompetitionQueue = async () => {
    const { data, error } = await supabase
      .from('competitor_search_queue')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(20);

    if (error) {
      console.error('Error fetching competition queue:', error);
    } else {
      setCompetitionQueue(data || []);
    }
  };

  const fetchSubmissions = async () => {
    setLoading(true);
    let query = supabase
      .from('lead_submissions')
      .select('*')
      .is('quiz_submission_id', null); // Only show lead submissions NOT linked to quiz
    
    if (!showTestData) {
      query = query.eq('is_test_data', false);
    }
    
    // Filter by source project
    if (sourceProjectFilter !== 'all') {
      if (sourceProjectFilter === 'lifactor') {
        query = query.or('source_project.eq.lifactor,source_project.is.null');
      } else {
        query = query.eq('source_project', sourceProjectFilter);
      }
    }
    
    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) {
      toast({
        title: "Błąd",
        description: "Nie udało się pobrać danych",
        variant: "destructive"
      });
    } else {
      setSubmissions(data || []);
    }
    setLoading(false);
  };

  const fetchQuizSubmissions = async () => {
    setLoading(true);
    let query = supabase
      .from('quiz_submissions')
      .select(`
        *,
        lead_submission:lead_submissions!quiz_submissions_converted_to_lead_id_fkey(*)
      `);
    
    if (!showTestData) {
      query = query.eq('is_test_data', false);
    }
    
    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) {
      console.error('❌ Error fetching quiz submissions:', error);
      toast({
        title: "Błąd",
        description: "Nie udało się pobrać quizów",
        variant: "destructive"
      });
    } else {
      console.log('✅ Fetched quiz submissions:', data?.length || 0);
      const items = (data || []) as QuizSubmission[];
      // Merge duplicates for the same session & domain – prefer rows with lead/email
      const mergedMap = new Map<string, QuizSubmission>();
      for (const quiz of items) {
        const key = `${quiz.session_id || ''}::${quiz.domain_input || ''}`;
        const existing = mergedMap.get(key);
        if (!existing) {
          mergedMap.set(key, quiz);
          continue;
        }
        const existingHasLead = !!(existing as any).lead_submission;
        const quizHasLead = !!(quiz as any).lead_submission;
        const existingHasEmail = !!existing.report_download_email;
        const quizHasEmail = !!quiz.report_download_email;
        const existingCreated = new Date(existing.created_at).getTime();
        const quizCreated = new Date(quiz.created_at).getTime();
        const shouldReplace =
          (!existingHasLead && quizHasLead) ||
          (!existingHasEmail && quizHasEmail) ||
          quizCreated > existingCreated;
        if (shouldReplace) {
          mergedMap.set(key, quiz);
        }
      }
      setQuizSubmissions(Array.from(mergedMap.values()));
    }
    setLoading(false);
  };

  const fetchSocialSellingSubmissions = async () => {
    setLoading(true);
    // Disable cache to always fetch fresh data
    const { data, error } = await supabase
      .from('social_selling_submissions')
      .select('*')
      .gte('created_at', '2000-01-01') // Dummy filter to bypass cache
      .order('created_at', { ascending: false });

    if (error) {
      toast({
        title: "Błąd",
        description: "Nie udało się pobrać zgłoszeń social selling",
        variant: "destructive"
      });
    } else {
      setSocialSellingSubmissions(data || []);
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    const { error } = await supabase
      .from('lead_submissions')
      .delete()
      .eq('id', deleteId);

    if (error) {
      toast({
        title: "Błąd",
        description: "Nie udało się usunąć rekordu",
        variant: "destructive"
      });
    } else {
      toast({
        title: "Sukces",
        description: "Rekord został usunięty"
      });
      fetchSubmissions();
    }
    setDeleteId(null);
  };

  const handleDeleteQuiz = async (quizId: string) => {
    const { error } = await deleteQuizGroup(quizId);

    if (error) {
      console.error('Delete quiz error:', error);
      toast({
        title: "Błąd",
        description: `Nie udało się usunąć quizu: ${error.message}`,
        variant: "destructive"
      });
    } else {
      toast({
        title: "Sukces",
        description: "Quiz został usunięty razem z ukrytymi duplikatami"
      });
      fetchQuizSubmissions();
    }
  };

  const handleDeleteSocialSelling = async (id: string) => {
    const { error } = await supabase
      .from('social_selling_submissions')
      .delete()
      .eq('id', id);

    if (error) {
      toast({
        title: "Błąd",
        description: "Nie udało się usunąć zgłoszenia",
        variant: "destructive"
      });
    } else {
      toast({
        title: "Sukces",
        description: "Zgłoszenie zostało usunięte"
      });
      fetchSocialSellingSubmissions();
    }
  };

  const handleStartAudit = async (submission: SocialSellingSubmission) => {
    try {
      setSelectedAuditId(submission.id);
      setProgressDialogOpen(true);

      const { error } = await supabase.functions.invoke('audit-social-selling', {
        body: { 
          submissionId: submission.id,
          useMockData: useMockEnrichB2B
        }
      });

      if (error) throw error;
      
      toast({
        title: "Sukces",
        description: "Audyt rozpoczęty"
      });
      fetchSocialSellingSubmissions();
    } catch (error: any) {
      console.error('Error starting audit:', error);
      toast({
        title: "Błąd",
        description: "Błąd podczas rozpoczynania audytu",
        variant: "destructive"
      });
      setProgressDialogOpen(false);
    }
  };

  const handleViewReport = (submission: SocialSellingSubmission) => {
    if (submission.report_data) {
      navigate(`/raport-socialselling/raport/${submission.id}`);
    }
  };

  const handleRetryAudit = async (submissionId: string) => {
    if (!submissionId) {
      toast({
        title: "Błąd",
        description: "Brak ID submisji",
        variant: "destructive"
      });
      return;
    }
    
    try {
      console.log('🔄 Calling retry-audit with:', { submissionId });
      
      // Call retry-audit with 10-second timeout
      const { data, error } = await supabase.functions.invoke('retry-audit', {
        body: { submissionId },
      });
      
      console.log('📤 Retry-audit response:', { data, error });

      // Check submission status before treating as error
      const { data: submission } = await supabase
        .from('social_selling_submissions')
        .select('status')
        .eq('id', submissionId)
        .single();

      // If function timed out but submission is pending/processing, it's actually running
      if (error && submission?.status && ['pending', 'processing'].includes(submission.status)) {
        toast({
          title: "Audit w toku",
          description: "Przetwarzanie trwa w tle...",
        });
        
        // Start polling for status
        startStatusPolling(submissionId);
        return;
      }

      if (error) throw error;

      toast({
        title: "Audit ponownie uruchomiony",
        description: "Przetwarzanie rozpoczęte",
      });

      // Start polling for status updates
      startStatusPolling(submissionId);
      
    } catch (err: any) {
      console.error('Retry audit error:', err);
      toast({
        title: "Błąd",
        description: err.message || "Nie udało się ponowić audytu",
        variant: "destructive"
      });
    }
  };

  const startStatusPolling = (submissionId: string) => {
    let pollCount = 0;
    const maxPolls = 120; // 120 * 3 seconds = 6 minutes max

    const pollInterval = setInterval(async () => {
      pollCount++;
      
      try {
        const { data: submission } = await supabase
          .from('social_selling_submissions')
          .select('status, report_data')
          .eq('id', submissionId)
          .single();

        if (!submission) {
          clearInterval(pollInterval);
          return;
        }

        // If completed or failed, stop polling and refresh
        if (submission.status === 'completed' || submission.status === 'failed') {
          clearInterval(pollInterval);
          
          toast({
            title: submission.status === 'completed' ? "Audit zakończony" : "Audit nie powiódł się",
            description: submission.status === 'completed' 
              ? "Raport jest gotowy do przeglądania" 
              : "Sprawdź logi aby zobaczyć szczegóły",
            variant: submission.status === 'completed' ? 'default' : 'destructive'
          });

          fetchSocialSellingSubmissions();
        }

        // Stop after max polls
        if (pollCount >= maxPolls) {
          clearInterval(pollInterval);
          toast({
            title: "Timeout",
            description: "Audyt trwa dłużej niż oczekiwano. Odśwież stronę aby sprawdzić status.",
            variant: "destructive"
          });
        }
      } catch (pollError) {
        console.error('Status polling error:', pollError);
      }
    }, 3000); // Poll every 3 seconds
  };

  const handleSelfTest = async () => {
    try {
      // Pobierz ostatni rekord z social_selling_submissions
      const { data: submissions, error: fetchError } = await supabase
        .from('social_selling_submissions')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1);

      if (fetchError) throw fetchError;
      
      if (!submissions || submissions.length === 0) {
        toast({
          title: "Brak rekordów",
          description: "Nie ma żadnych zgłoszeń social selling",
          variant: "destructive"
        });
        return;
      }

      const lastSubmission = submissions[0];
      
      toast({
        title: "Self Test",
        description: `Rozpoczynam retry dla: ${lastSubmission.user_domain}`,
      });

      // Wywołaj retry-audit dla ostatniego rekordu
      await handleRetryAudit(lastSubmission.id);
      
    } catch (err: any) {
      console.error('Self test error:', err);
      toast({
        title: "Błąd Self Test",
        description: err.message || "Nie udało się wykonać self test",
        variant: "destructive"
      });
    }
  };

  const handleAuditComplete = () => {
    setActiveAuditId(null);
    fetchSocialSellingSubmissions();
    toast({
      title: "Sukces!",
      description: "Audyt został zakończony",
    });
  };

  const handleFixStuckAudits = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('fix-stuck-audits', { body: {} });
      if (error) throw error;
      toast({
        title: "Naprawiono zawieszone audyty",
        description: `Zaktualizowano: ${data?.fixed ?? 0}`,
      });
      fetchSocialSellingSubmissions();
    } catch (err: any) {
      console.error('Fix stuck audits error:', err);
      toast({
        title: "Błąd",
        description: err.message || "Nie udało się naprawić audytów",
        variant: "destructive"
      });
    }
  };

  const addToEnrichmentQueue = (leadId: string, manualDomain?: string | null, manualKrs?: string | null) => {
    setEnrichmentQueue(prev => [...prev, { leadId, manualDomain, manualKrs }]);
    setEnrichmentProgress(prev => ({
      ...prev,
      [leadId]: { status: 'queued' }
    }));
  };

  const pollEnrichmentStatus = async (leadId: string): Promise<void> => {
    const maxAttempts = 60; // 60 * 5s = 5 minut max
    let attempts = 0;
    
    return new Promise((resolve, reject) => {
      const interval = setInterval(async () => {
        attempts++;
        
        try {
          const { data: lead, error } = await supabase
            .from('lead_submissions')
            .select('status, enriched_data')
            .eq('id', leadId)
            .single();
          
          if (error) throw error;
          
          // Zakończono
          if (lead.status === 'completed') {
            clearInterval(interval);
            setEnrichmentProgress(prev => ({
              ...prev,
              [leadId]: { 
                status: 'completed',
                completedAt: Date.now()
              }
            }));
            fetchSubmissions(); // Odśwież tabelę
            resolve();
          }
          
          // Błąd
          if (lead.status === 'error') {
            clearInterval(interval);
            setEnrichmentProgress(prev => ({
              ...prev,
              [leadId]: { 
                status: 'error',
                error: 'Enrichment failed',
                completedAt: Date.now()
              }
            }));
            reject(new Error('Enrichment failed'));
          }
          
          // Timeout
          if (attempts >= maxAttempts) {
            clearInterval(interval);
            reject(new Error('Polling timeout'));
          }
          
        } catch (pollError) {
          clearInterval(interval);
          reject(pollError);
        }
      }, 5000); // Poll co 5 sekund
    });
  };

  const processEnrichmentQueue = async () => {
    // Jeśli coś się przetwarza, czekaj
    if (currentlyEnriching) return;
    
    // Pobierz następny lead z kolejki
    const nextItem = enrichmentQueue[0];
    if (!nextItem) return;
    
    const { leadId, manualDomain, manualKrs } = nextItem;
    
    setCurrentlyEnriching(leadId);
    setEnrichingId(leadId);
    setEnrichmentProgress(prev => ({
      ...prev,
      [leadId]: { status: 'processing', startedAt: Date.now() }
    }));
    
    try {
      // Wywołaj edge function z opcjonalnymi override parametrami
      const { data, error } = await supabase.functions.invoke('enrich-lead', {
        body: { 
          leadId, 
          immediate: false,
          overrideDomain: manualDomain || undefined,
          overrideKrs: manualKrs || undefined
        }
      });
      
      if (error) throw error;
      
      // Rozpocznij polling statusu
      await pollEnrichmentStatus(leadId);
      
      toast({
        title: "Wzbogacanie zakończone",
        description: `Lead został wzbogacony`,
      });
      
    } catch (error: any) {
      logger.error('Enrich error', error);
      setEnrichmentProgress(prev => ({
        ...prev,
        [leadId]: { 
          status: 'error', 
          error: error.message,
          completedAt: Date.now()
        }
      }));
      toast({
        title: "Błąd wzbogacania",
        description: error.message || "Nie udało się wzbogacić danych",
        variant: "destructive"
      });
    } finally {
      // Usuń z kolejki i przejdź do następnego
      setEnrichmentQueue(prev => prev.slice(1));
      setCurrentlyEnriching(null);
      setEnrichingId(null);
    }
  };

  // Private email domains that need manual data input
  const PRIVATE_EMAIL_DOMAINS = ['gmail.com', 'yahoo.com', 'wp.pl', 'onet.pl', 'o2.pl', 'interia.pl', 'outlook.com', 'hotmail.com', 'icloud.com', 'me.com', 'aol.com', 'live.com', 'msn.com', 'protonmail.com'];
  
  const isPrivateEmail = (email: string | null | undefined): boolean => {
    if (!email) return true;
    const domain = email.toLowerCase().split('@')[1];
    return PRIVATE_EMAIL_DOMAINS.some(d => domain === d || domain?.endsWith(`.${d}`));
  };

  const handleEnrich = async (submission: LeadSubmission) => {
    // Check if email is private (Gmail, etc.) - open modal for manual input
    if (isPrivateEmail(submission.email)) {
      setEnrichmentModal({
        isOpen: true,
        submission,
        manualDomain: '',
        manualKrs: ''
      });
      return;
    }
    
    // Corporate email - proceed with standard enrichment
    addToEnrichmentQueue(submission.id, null, null);
    
    toast({
      title: "Dodano do kolejki",
      description: `Lead ${submission.name} został dodany do kolejki wzbogacania`,
    });
  };

  const handleEnrichWithManualData = () => {
    if (!enrichmentModal.submission) return;
    
    const { submission, manualDomain, manualKrs } = enrichmentModal;
    
    addToEnrichmentQueue(
      submission.id,
      manualDomain.trim() || null,
      manualKrs.trim() || null
    );
    
    toast({
      title: "Dodano do kolejki",
      description: `Lead ${submission.name} zostanie wzbogacony${manualDomain ? ` (domena: ${manualDomain})` : ''}${manualKrs ? ` (KRS: ${manualKrs})` : ''}`,
    });
    
    setEnrichmentModal({ isOpen: false, submission: null, manualDomain: '', manualKrs: '' });
  };

  const handleViewEnrichedData = (submission: LeadSubmission) => {
    setSelectedSubmission(submission);
    setShowLeadEnrichedDialog(true);
  };

  const handleViewQuizAnswers = (submission: LeadSubmission) => {
    setSelectedSubmission(submission);
    setShowQuizDialog(true);
  };

  const handleViewQuizEnrichedData = (quiz: QuizSubmission) => {
    setSelectedQuizSubmission(quiz);
    setShowQuizEnrichedDialog(true);
  };

  const handlePushToCRM = async (id: string) => {
    try {
      setPushingToSalesflareId(id);
      
      console.log('Pushing lead to Salesflare:', id);
      
      const { data, error } = await supabase.functions.invoke('push-to-salesflare', {
        body: { 
          leadId: id,
          linkedinUrlFieldId: 562419,
          bizraportUrlFieldId: 562420
        }
      });

      if (error) throw error;

      console.log('Salesflare push result:', data);

      toast({
        title: "Sukces!",
        description: `Lead został wysłany do Salesflare jako "${data.opportunityName}"`
      });

      // Refresh submissions to show updated CRM status
      fetchSubmissions();

    } catch (error: any) {
      console.error('Error pushing to Salesflare:', error);
      toast({
        title: "Błąd",
        description: error.message || "Nie udało się wysłać leadu do Salesflare",
        variant: "destructive"
      });
    } finally {
      setPushingToSalesflareId(null);
    }
  };

  const handleEnrichQuiz = async (quiz: QuizSubmission) => {
    try {
      setEnrichingId(quiz.id);
      
      console.log('Enriching quiz:', quiz.id);
      
      const { data, error } = await supabase.functions.invoke('enrich-quiz', {
        body: { quizId: quiz.id }
      });

      if (error) throw error;

      console.log('Quiz enrichment result:', data);

      toast({
        title: "Rozpoczęto wzbogacanie",
        description: "Quiz zostanie wzbogacony danymi AI"
      });

      // Refresh quiz submissions to show updated enrichment status
      fetchQuizSubmissions();

    } catch (error: any) {
      console.error('Error enriching quiz:', error);
      toast({
        title: "Błąd",
        description: error.message || "Nie udało się wzbogacić quizu",
        variant: "destructive"
      });
    } finally {
      setEnrichingId(null);
    }
  };

  const handlePushQuizToSalesflare = async (quiz: QuizSubmission) => {
    if (!quiz.report_download_email) {
      toast({
        title: "Brak danych kontaktowych",
        description: "Quiz musi mieć email aby wysłać do CRM",
        variant: "destructive"
      });
      return;
    }

    try {
      setPushingToSalesflareId(quiz.id);
      
      console.log('Pushing quiz to Salesflare:', quiz.id);
      
      const { data, error } = await supabase.functions.invoke('push-quiz-to-salesflare', {
        body: { 
          quizId: quiz.id,
          contactData: {
            name: `Quiz Lead - ${quiz.domain_input || 'Unknown'}`,
            phone: '+48000000000',
            company: quiz.domain_input || undefined
          }
        }
      });

      if (error) throw error;

      console.log('Salesflare push result:', data);

      toast({
        title: "Sukces!",
        description: `Quiz został wysłany do Salesflare`
      });

      // Refresh quiz submissions to show updated CRM status
      fetchQuizSubmissions();

    } catch (error: any) {
      console.error('Error pushing quiz to Salesflare:', error);
      toast({
        title: "Błąd",
        description: error.message || "Nie udało się wysłać quizu do Salesflare",
        variant: "destructive"
      });
    } finally {
      setPushingToSalesflareId(null);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/auth');
  };

  const passwordSchema = z.string()
    .min(6, "Hasło musi mieć minimum 6 znaków")
    .max(72, "Hasło może mieć maksymalnie 72 znaki");

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast({
        title: "Błąd",
        description: "Hasła nie są identyczne",
        variant: "destructive"
      });
      return;
    }

    const validation = passwordSchema.safeParse(newPassword);
    if (!validation.success) {
      toast({
        title: "Błąd walidacji",
        description: validation.error.errors[0].message,
        variant: "destructive"
      });
      return;
    }

    setChangingPassword(true);
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });

    if (error) {
      toast({
        title: "Błąd",
        description: error.message,
        variant: "destructive"
      });
    } else {
      toast({
        title: "Sukces!",
        description: "Hasło zostało zmienione"
      });
      setShowPasswordDialog(false);
      setNewPassword("");
      setConfirmPassword("");
    }
    setChangingPassword(false);
  };

  // Normalize domain - same logic as Quiz.tsx
  const normalizeDomain = (domain: string): string => {
    return domain.trim()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .replace(/\/$/, '');
  };

  const handleTestCompetitors = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!testDomain.trim()) {
      toast({
        title: "Błąd",
        description: "Podaj domenę do przetestowania",
        variant: "destructive"
      });
      return;
    }

    const normalizedDomain = normalizeDomain(testDomain);
    setTestingCompetitors(true);
    setTestResult(null);
    setTestStage('analyzing');
    setTestProgress(10);

    // Simulate progress stages - stop at 90% to avoid false "completion" feel
    const progressInterval = setInterval(() => {
      setTestProgress(prev => {
        if (prev < 90) return prev + 8; // Slower progress
        return 90; // Stop at 90% until actual response
      });
    }, 3000); // Slower interval (3s instead of 2s)

    const stageTimeout1 = setTimeout(() => setTestStage('searching'), 5000);
    const stageTimeout2 = setTimeout(() => setTestStage('verifying'), 15000);

    try {
      logger.info(`Testing competitor AI for domain: ${normalizedDomain}`);
      
      // Call with user-configured settings
      const invokeBody: any = { 
        domain: normalizedDomain, 
        forceRefresh: forceRefresh,
        fastMode: true,
        skipStep3LLM: !enableStep3LLM
      };

      // Add long timeout overrides if enabled
      if (longTimeouts) {
        invokeBody.step1TimeoutSec = 200;
        invokeBody.step2TimeoutSec = 200;
        invokeBody.step3TimeoutSec = 500;
      }

      const { data, error } = await supabase.functions.invoke('find-competitors', {
        body: invokeBody
      });

      clearInterval(progressInterval);
      clearTimeout(stageTimeout1);
      clearTimeout(stageTimeout2);
      setTestProgress(100);
      setTestStage('complete');

      if (error) {
        throw error;
      }

      // Check for success: false in 200 response
      if (data && data.success === false) {
        const errorMsg = longTimeouts 
          ? `[Diagnostic run] ${data.error || "Analysis completed with warnings"}` 
          : data.error || "Analysis completed with warnings";
        
        toast({
          title: "⚠️ Competitor Analysis Issue",
          description: errorMsg,
          variant: "destructive"
        });
      }

      setTestResult(data);
      
      toast({
        title: data.competitors?.length > 0 
          ? `✅ Znaleziono ${data.competitors.length} konkurentów` 
          : "⚠️ Nie znaleziono konkurentów",
        description: `Confidence: ${data.confidence || 'unknown'}`,
        variant: data.competitors?.length > 0 ? "default" : "destructive"
      });
      
      logger.debug('Competitor test result:', data);
      
    } catch (err: any) {
      clearInterval(progressInterval);
      clearTimeout(stageTimeout1);
      clearTimeout(stageTimeout2);
      
      logger.error('Competitor test error', err);
      toast({
        title: "❌ Test Failed",
        description: err.message || "Wystąpił błąd podczas testowania",
        variant: "destructive"
      });
      setTestResult({ error: err.message });
    } finally {
      setTestingCompetitors(false);
    }
  };

  const resetCompetitorTest = () => {
    setTestDomain("");
    setTestResult(null);
    setTestProgress(0);
    setTestStage('analyzing');
    setForceRefresh(false);
    setLongTimeouts(false);
    setEnableStep3LLM(false);
  };

  const handleDomainChange = (index: number, value: string) => {
    const newDomains = [...searchDomains];
    newDomains[index] = value;
    setSearchDomains(newDomains);
  };

  const handleAddDomain = () => {
    setSearchDomains([...searchDomains, '']);
  };

  const handleRemoveDomain = (index: number) => {
    if (searchDomains.length > 1) {
      const newDomains = searchDomains.filter((_, i) => i !== index);
      setSearchDomains(newDomains);
    }
  };

  const handleSearchLinkedIn = async () => {
    const validDomains = searchDomains.filter(d => d.trim() !== '');
    
    if (validDomains.length === 0) {
      toast({
        title: "Błąd",
        description: "Wprowadź przynajmniej jedną domenę",
        variant: "destructive"
      });
      return;
    }

    setSearchingLinkedIn(true);
    setSearchResults([]);

    try {
      console.log('Searching LinkedIn for domains:', validDomains);
      
      const { data, error } = await supabase.functions.invoke('search-linkedin-companies', {
        body: { domains: validDomains }
      });

      if (error) {
        console.error('Function invocation error:', error);
        throw error;
      }

      console.log('Search results:', data);
      
      if (data.success && data.results) {
        setSearchResults(data.results);
        
        const foundCount = data.results.filter((r: any) => r.status === 'found').length;
        toast({
          title: "✅ Wyszukiwanie zakończone",
          description: `Znaleziono ${foundCount} z ${validDomains.length} profili LinkedIn`
        });
      } else {
        throw new Error(data.error || 'Unknown error');
      }
    } catch (err: any) {
      console.error('Search error:', err);
      toast({
        title: "❌ Błąd wyszukiwania",
        description: err.message || "Nie udało się wyszukać profili LinkedIn",
        variant: "destructive"
      });
    } finally {
      setSearchingLinkedIn(false);
    }
  };

  const handleCopyAllLinks = () => {
    const links = searchResults
      .filter(r => r.linkedinUrl)
      .map(r => r.linkedinUrl)
      .join('\n');
    
    if (links) {
      navigator.clipboard.writeText(links);
      toast({
        title: "✅ Skopiowano",
        description: `Skopiowano ${searchResults.filter(r => r.linkedinUrl).length} linków`
      });
    }
  };

  const handleSearchCompetitorLinkedIn = async (domain: string) => {
    // Set loading state for this domain
    setCompetitorLinkedInUrls(prev => ({
      ...prev,
      [domain]: { url: null, status: 'loading' }
    }));

    try {
      console.log('Searching LinkedIn for competitor domain:', domain);
      
      const { data, error } = await supabase.functions.invoke('search-linkedin-companies', {
        body: { domains: [domain] }
      });

      if (error) {
        console.error('Function invocation error:', error);
        throw error;
      }

      console.log('Search result:', data);
      
      if (data.success && data.results && data.results.length > 0) {
        const result = data.results[0];
        setCompetitorLinkedInUrls(prev => ({
          ...prev,
          [domain]: {
            url: result.linkedinUrl,
            status: result.status === 'found' ? 'found' : 'not_found',
            title: result.title
          }
        }));
        
        // Save to competitor_analyses if LinkedIn URL was found
        if (result.status === 'found' && result.linkedinUrl && competitionResults) {
          const targetDomain = competitionResults.metadata?.domain || competitionDomain;
          
          // Get existing analysis
          const { data: existingAnalysis } = await supabase
            .from('competitor_analyses')
            .select('competitors')
            .eq('domain', targetDomain)
            .single();
          
          if (existingAnalysis) {
            // Update competitors array with LinkedIn data
            const updatedCompetitors = (existingAnalysis.competitors as any[]).map((comp: any) => {
              if (comp.domain === domain) {
                return {
                  ...comp,
                  linkedInUrl: result.linkedinUrl,
                  linkedInTitle: result.title
                };
              }
              return comp;
            });
            
            // Update the database
            await supabase
              .from('competitor_analyses')
              .update({ competitors: updatedCompetitors })
              .eq('domain', targetDomain);
            
            console.log('✅ LinkedIn URL saved to competitor_analyses');
          }
        }
        
        if (result.status === 'found') {
          toast({
            title: "✅ Znaleziono LinkedIn",
            description: result.title || "Profil LinkedIn został znaleziony"
          });
        } else {
          toast({
            title: "⚠️ Nie znaleziono",
            description: "Nie udało się znaleźć profilu LinkedIn dla tej domeny",
            variant: "destructive"
          });
        }
      } else {
        throw new Error(data.error || 'Unknown error');
      }
    } catch (err: any) {
      console.error('Search error:', err);
      setCompetitorLinkedInUrls(prev => ({
        ...prev,
        [domain]: { url: null, status: 'error' }
      }));
      toast({
        title: "❌ Błąd wyszukiwania",
        description: err.message || "Nie udało się wyszukać profilu LinkedIn",
        variant: "destructive"
      });
    }
  };

  const handleSearchCompetition = async () => {
    // Wyczyść domenę z protokołu i trailing slash
    const domain = competitionDomain
      .trim()
      .replace(/^https?:\/\//, '')  // Usuń http:// lub https://
      .replace(/\/$/, '');           // Usuń trailing slash
    
    if (!domain) {
      toast({
        title: "Błąd",
        description: "Wprowadź domenę firmy",
        variant: "destructive"
      });
      return;
    }

    setSearchingCompetition(true);
    setCompetitorLinkedInUrls({}); // Reset LinkedIn URLs when searching new competition

    try {
      // Add job to queue
      const { data: newJob, error: insertError } = await supabase
        .from('competitor_search_queue')
        .insert({
          domain: domain,
          fast_mode: fastMode,
          status: 'queued'
        })
        .select()
        .single();

      if (insertError) throw insertError;

      toast({
        title: "✅ Dodano do kolejki",
        description: `Zadanie dla ${domain} zostało dodane do kolejki`
      });
      
      // Clear input
      setCompetitionDomain('');
      
      // Refresh queue
      await fetchCompetitionQueue();
      
      // Trigger processing
      supabase.functions.invoke('process-competitor-queue', {});
      
    } catch (err: any) {
      console.error('Error adding to queue:', err);
      toast({
        title: "❌ Błąd",
        description: err.message || "Nie udało się dodać do kolejki",
        variant: "destructive"
      });
    } finally {
      setSearchingCompetition(false);
    }
  };
  
  const handleViewQueueResult = async (job: any) => {
    console.log('View queue result:', job);
    
    // Handle new multi-step queue result format
    if (job.result_data && job.result_data.search_results && job.result_data.search_results.local_competitors) {
      const localCompetitors = job.result_data.search_results.local_competitors;
      
      setCompetitionResults({
        success: true,
        confidence: 'high',
        competitors: localCompetitors.map((comp: any, index: number) => ({
          name: comp.company_name || comp.domain,
          domain: comp.domain,
          score: Math.round((1 - (index * 0.05)) * 100), // Decreasing score based on position
          competition_level: comp.business_model_similarity >= 0.8 ? 'direct' : 
                           comp.business_model_similarity >= 0.6 ? 'indirect' : 'potential'
        })),
        company_name: job.result_data.target_company_name || job.domain,
        industry: job.result_data.target_industry || 'N/A',
        metadata: job.result_data || {}
      });
      
      // Load existing LinkedIn URLs from competitor_analyses
      await loadLinkedInUrlsFromAnalysis(job.domain);
      
      toast({
        title: "Wyniki załadowane",
        description: `Znaleziono ${localCompetitors.length} konkurentów`
      });
    }
    // Handle legacy format (from old find-competitors)
    else if (job.result_data && job.result_data.competitors) {
      setCompetitionResults({
        success: job.result_data.success,
        confidence: job.result_data.confidence || 'medium',
        competitors: job.result_data.competitors,
        company_name: job.result_data.company_name || job.domain,
        industry: job.result_data.industry || 'N/A',
        metadata: job.result_data.metadata || {}
      });
      
      // Load existing LinkedIn URLs from competitor_analyses
      await loadLinkedInUrlsFromAnalysis(job.domain);
      
      toast({
        title: "Wyniki załadowane",
        description: `Znaleziono ${job.result_data.competitors.length} konkurentów`
      });
    } else {
      toast({
        title: "Brak wyników",
        description: "Nie znaleziono danych konkurencji w zadaniu",
        variant: "destructive"
      });
      console.error('Invalid result_data structure:', job.result_data);
    }
  };
  
  const loadLinkedInUrlsFromAnalysis = async (domain: string) => {
    try {
      const { data: analysis } = await supabase
        .from('competitor_analyses')
        .select('competitors')
        .eq('domain', domain)
        .single();
      
      if (analysis && analysis.competitors) {
        const linkedInData: Record<string, { url: string | null; status: 'found' | 'not_found' | 'error' | 'loading'; title?: string }> = {};
        
        (analysis.competitors as any[]).forEach((comp: any) => {
          if (comp.linkedInUrl) {
            linkedInData[comp.domain] = {
              url: comp.linkedInUrl,
              status: 'found',
              title: comp.linkedInTitle
            };
          }
        });
        
        setCompetitorLinkedInUrls(linkedInData);
        console.log('✅ Loaded LinkedIn URLs from competitor_analyses:', linkedInData);
      }
    } catch (error) {
      console.error('Error loading LinkedIn URLs from analysis:', error);
    }
  };
  
  const handleRetryQueueJob = async (jobId: string) => {
    try {
      const { error } = await supabase
        .from('competitor_search_queue')
        .update({
          status: 'queued',
          error_message: null,
          progress: null,
          progress_percent: 0,
          started_at: null,
          completed_at: null
        })
        .eq('id', jobId);

      if (error) throw error;

      toast({
        title: "✅ Ponowienie zadania",
        description: "Zadanie zostało dodane ponownie do kolejki"
      });
      
      await fetchCompetitionQueue();
      supabase.functions.invoke('process-competitor-queue', {});
      
    } catch (err: any) {
      toast({
        title: "❌ Błąd",
        description: err.message || "Nie udało się ponowić zadania",
        variant: "destructive"
      });
    }
  };
  
  const handleDeleteQueueJob = async (jobId: string) => {
    try {
      const { error } = await supabase
        .from('competitor_search_queue')
        .delete()
        .eq('id', jobId);

      if (error) throw error;

      toast({
        title: "✅ Usunięto",
        description: "Zadanie zostało usunięte z kolejki"
      });
      
      await fetchCompetitionQueue();
      
    } catch (err: any) {
      toast({
        title: "❌ Błąd",
        description: err.message || "Nie udało się usunąć zadania",
        variant: "destructive"
      });
    }
  };

  const handleCopyAllCompetitorDomains = () => {
    if (!competitionResults || !competitionResults.competitors) return;
    
    const domains = competitionResults.competitors
      .map(c => c.domain)
      .join('\n');
    
    if (domains) {
      navigator.clipboard.writeText(domains);
      toast({
        title: "✅ Skopiowano",
        description: `Skopiowano ${competitionResults.competitors.length} domen`
      });
    }
  };

  const handleTestApiConnections = async () => {
    setTestingApis(true);
    setApiCheckResults(null);
    
    try {
      console.log('Starting API test connections...');
      
      // Add timeout of 30 seconds
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout after 30 seconds')), 30000)
      );
      
      const invokePromise = supabase.functions.invoke('test-api-connections');
      
      const { data, error } = await Promise.race([invokePromise, timeoutPromise]) as any;
      
      if (error) {
        console.error('Edge function error:', error);
        throw error;
      }
      
      console.log('API test results:', data);
      
      setApiCheckResults(data);
      setShowApiCheckDialog(true);
      
      if (data.summary?.allOk) {
        toast({
          title: "✅ Wszystkie API działają",
          description: `${data.summary.success}/${data.summary.total} połączeń OK`,
        });
      } else {
        toast({
          title: "⚠️ Problemy z API",
          description: `Błędów: ${data.summary.error}, Ostrzeżeń: ${data.summary.warning}`,
          variant: "destructive"
        });
      }
    } catch (err: any) {
      console.error('Full error details:', err);
      toast({
        title: "❌ Test Failed",
        description: err.message || "Nie udało się przetestować API",
        variant: "destructive"
      });
    } finally {
      setTestingApis(false);
    }
  };

  const handleTestSalesflare = async () => {
    setLoading(true);
    setSalesflareTestResult(null);
    
    try {
      console.log('Inspecting Salesflare structure...');
      
      const { data, error } = await supabase.functions.invoke('inspect-salesflare-structure');
      
      if (error) {
        console.error('Salesflare inspect error:', error);
        throw error;
      }
      
      console.log('Salesflare structure:', data);
      
      setSalesflareTestResult(data);
      setShowSalesflareDialog(true);
      
      const socialSellingPipeline = data.summary?.socialSellingPipeline;
      
      toast({
        title: socialSellingPipeline 
          ? `✅ Znaleziono pipeline: ${socialSellingPipeline.name}` 
          : "⚠️ Nie znaleziono 'Social Selling' pipeline",
        description: `Pipelines: ${data.summary?.totalPipelines}, Stages: ${data.summary?.totalStages}, Custom Fields: ${data.summary?.totalCustomFields}`,
        variant: socialSellingPipeline ? "default" : "destructive"
      });
    } catch (err: any) {
      console.error('Salesflare test error:', err);
      toast({
        title: "❌ Salesflare Test Failed",
        description: err.message || "Nie udało się połączyć z Salesflare",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTestCompanyUrl = async () => {
    if (!testCompanyUrl.trim()) {
      toast({
        title: "❌ Error",
        description: "Please enter a LinkedIn company URL",
        variant: "destructive"
      });
      return;
    }

    setTestingCompanyUrl(true);
    setCompanyTestResult(null);

    try {
      console.log('Testing B2B Enrich with URL:', testCompanyUrl);
      
      const namesParsed = companyNames
        .split(',')
        .map(n => n.trim())
        .filter(Boolean);

      const { data, error } = await supabase.functions.invoke('test-enrichb2b-manual', {
        body: { 
          companyLinkedInUrl: testCompanyUrl,
          searchName: searchName || undefined,
          names: namesParsed.length > 0 ? namesParsed : undefined
        }
      });

      if (error) {
        console.error('Function invocation error:', error);
        throw error;
      }

      console.log('B2B Test Result:', data);
      setCompanyTestResult(data);

      if (data.success) {
        toast({
          title: "✅ Success",
          description: `Found ${data.apiResponse?.data?.result?.length || 0} employees`,
        });
      } else {
        toast({
          title: "❌ API Error",
          description: data.error || "Failed to fetch employees",
          variant: "destructive"
        });
      }
    } catch (err: any) {
      console.error('Test error:', err);
      setCompanyTestResult({
        success: false,
        error: err.message || "Failed to test company URL"
      });
      toast({
        title: "❌ Test Failed",
        description: err.message || "Failed to test company URL",
        variant: "destructive"
      });
    } finally {
      setTestingCompanyUrl(false);
    }
  };

  const handleTestEnrichB2B = async () => {
    setTestingApis(true);
    setApiCheckResults(null);
    
    try {
      console.log('Starting EnrichB2B API test...');
      
      const { data, error } = await supabase.functions.invoke('test-enrichb2b');
      
      if (error) {
        console.error('Edge function error:', error);
        throw error;
      }
      
      console.log('EnrichB2B test results:', data);
      
      setApiCheckResults(data);
      setShowApiCheckDialog(true);
      
      if (data.summary?.allOk) {
        toast({
          title: "✅ EnrichB2B API działa",
          description: `${data.summary.success}/${data.summary.total} testów OK, Kredyty: ${data.summary.creditsRemaining}`,
        });
      } else {
        toast({
          title: "⚠️ Problemy z EnrichB2B",
          description: `Błędów: ${data.summary.error}`,
          variant: "destructive"
        });
      }
    } catch (err: any) {
      console.error('Full error details:', err);
      toast({
        title: "❌ Test Failed",
        description: err.message || "Nie udało się przetestować EnrichB2B API",
        variant: "destructive"
      });
    } finally {
      setTestingApis(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const formatPhoneNumber = (phone: string) => {
    // Remove all non-digit characters
    const cleaned = phone.replace(/\D/g, '');
    
    // Format as +XX XXX XXX XXX for Polish numbers
    if (cleaned.length === 11 && cleaned.startsWith('48')) {
      return `+${cleaned.slice(0, 2)} ${cleaned.slice(2, 5)} ${cleaned.slice(5, 8)} ${cleaned.slice(8)}`;
    }
    
    // Format as XXX XXX XXX for 9-digit numbers
    if (cleaned.length === 9) {
      return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`;
    }
    
    // Return original if format doesn't match
    return phone;
  };

  const getBandColor = (band: string | null) => {
    if (!band) return "secondary";
    switch (band) {
      case 'high': return "default";
      case 'mid': return "secondary";
      case 'low': return "outline";
      default: return "secondary";
    }
  };

  const getBandLabel = (band: string | null) => {
    if (!band) return "N/A";
    switch (band) {
      case 'high': return "Wysoki";
      case 'mid': return "Średni";
      case 'low': return "Niski";
      default: return band;
    }
  };

  // Extract domain from quiz_answers
  const getDomainFromQuizAnswers = (submission: LeadSubmission): string | null => {
    if (!submission.quiz_answers) return null;
    const domain = submission.quiz_answers['4'] || submission.quiz_answers[4];
    if (!domain) return null;
    
    // Normalize domain
    return String(domain)
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .replace(/\/$/, '')
      .trim();
  };

  const handleUpdateQuizStatus = async (quizId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('quiz_submissions')
        .update({ status: newStatus })
        .eq('id', quizId);

      if (error) throw error;

      // Update local state
      setQuizSubmissions(prev =>
        prev.map(q => q.id === quizId ? { ...q, status: newStatus } : q)
      );

      toast({
        title: "Status zaktualizowany",
        description: "Status CRM został zmieniony"
      });
    } catch (error: any) {
      console.error('Error updating status:', error);
      toast({
        title: "Błąd",
        description: "Nie udało się zaktualizować statusu",
        variant: "destructive"
      });
    }
  };

  const handleUpdateQuizNotes = async (quizId: string, notes: string) => {
    try {
      const { error } = await supabase
        .from('quiz_submissions')
        .update({ notes })
        .eq('id', quizId);

      if (error) throw error;

      // Update local state
      setQuizSubmissions(prev =>
        prev.map(q => q.id === quizId ? { ...q, notes } : q)
      );

      toast({
        title: "Notatki zapisane",
        description: "Notatki zostały zaktualizowane"
      });
    } catch (error: any) {
      console.error('Error updating notes:', error);
      toast({
        title: "Błąd",
        description: "Nie udało się zapisać notatek",
        variant: "destructive"
      });
    }
  };

  const handleUpdateLeadStatus = async (leadId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('lead_submissions')
        .update({ crm_status: newStatus })
        .eq('id', leadId);

      if (error) throw error;

      setSubmissions(prev =>
        prev.map(l => l.id === leadId ? { ...l, crm_status: newStatus } : l)
      );

      toast({
        title: "Status zaktualizowany",
        description: "Status CRM został zmieniony"
      });
    } catch (error: any) {
      console.error('Error updating lead status:', error);
      toast({
        title: "Błąd",
        description: "Nie udało się zaktualizować statusu",
        variant: "destructive"
      });
    }
  };

  const handleUpdateLeadNotes = async (leadId: string, notes: string) => {
    try {
      const { error } = await supabase
        .from('lead_submissions')
        .update({ notes })
        .eq('id', leadId);

      if (error) throw error;

      setSubmissions(prev =>
        prev.map(l => l.id === leadId ? { ...l, notes } : l)
      );

      toast({
        title: "Notatki zapisane",
        description: "Notatki zostały zaktualizowane"
      });
    } catch (error: any) {
      console.error('Error updating lead notes:', error);
      toast({
        title: "Błąd",
        description: "Nie udało się zapisać notatek",
        variant: "destructive"
      });
    }
  };

  const handleGeneratePDF = async (submission: any) => {
    toast({
      title: "Generuję PDF...",
      description: "To może chwilę potrwać"
    });
    
    try {
      const leadData = {
        name: submission.name,
        email: submission.email,
        phone: submission.phone,
        company: submission.company,
        position: submission.position,
      };

      const quizData = {
        score: submission.quiz_score || 0,
        quiz_band: (submission.quiz_band || 'mid') as 'low' | 'mid' | 'high',
        domain: submission.domain_input || submission.company,
        quiz_answers: submission.quiz_answers,
        competitors: submission.selected_competitors || [],
        created_at: submission.created_at,
      };

      await generateQuizReportPDF(leadData, quizData);
      
      toast({
        title: "PDF wygenerowany!",
        description: "Raport został pobrany"
      });
    } catch (error) {
      console.error('PDF generation error:', error);
      toast({
        title: "Błąd generowania PDF",
        description: "Nie udało się wygenerować raportu",
        variant: "destructive"
      });
    }
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen gradient-subtle">
      <div className="container px-2 md:px-4 py-3 md:py-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 mb-4 md:mb-6">
          <h1 className="text-xl md:text-2xl font-bold">Admin</h1>
          <div className="flex flex-wrap gap-2 w-full md:w-auto">
            <Button variant="outline" size="sm" onClick={() => setShowPasswordDialog(true)}>
              <KeyRound className="h-4 w-4 md:mr-2" />
              <span className="hidden sm:inline">Hasło</span>
            </Button>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              <LogOut className="h-4 w-4 md:mr-2" />
              <span className="hidden sm:inline">Wyloguj</span>
            </Button>
          </div>
        </div>

        {/* Collapsible Testing Tools */}
        <Card className="mb-4">
          <CardContent className="p-3">
            <div className="flex items-center justify-between cursor-pointer" onClick={() => setShowTestingTools(!showTestingTools)}>
              <div className="flex items-center gap-2">
                <Beaker className="h-4 w-4" />
                <span className="font-semibold text-sm">Narzędzia testowe i API</span>
              </div>
              <Button variant="ghost" size="sm">
                {showTestingTools ? '▲' : '▼'}
              </Button>
            </div>
            
            {showTestingTools && (
              <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t">
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={handleTestApiConnections}
                  disabled={testingApis}
                >
                  {testingApis ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <Sparkles className="mr-2 h-3 w-3" />}
                  API Check
                </Button>
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={handleTestEnrichB2B}
                  disabled={testingApis}
                >
                  {testingApis ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <CheckCircle2 className="mr-2 h-3 w-3" />}
                  Test EnrichB2B
                </Button>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="use-mock-enrichb2b"
                    checked={useMockEnrichB2B}
                    onChange={(e) => setUseMockEnrichB2B(e.target.checked)}
                    className="h-3 w-3 rounded border-gray-300"
                  />
                  <Label htmlFor="use-mock-enrichb2b" className="text-xs cursor-pointer">
                    Use Mock EnrichB2B Data
                  </Label>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    setLoading(true);
                    try {
                      const { data, error } = await supabase.functions.invoke('test-openai');
                      if (error) throw error;
                      
                      toast({
                        title: data.success ? "✅ OpenAI Test" : "❌ OpenAI Test Failed",
                        description: data.responseContent || data.message || data.error,
                        variant: data.success ? "default" : "destructive"
                      });
                      logger.debug('OpenAI Test:', data);
                    } catch (err: any) {
                      toast({
                        title: "❌ Test Failed",
                        description: err.message,
                        variant: "destructive"
                      });
                      console.error('OpenAI Test Error:', err);
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading}
                >
                  {loading ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <Sparkles className="mr-2 h-3 w-3" />}
                  Test OpenAI
                </Button>
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    setLoading(true);
                    try {
                      const { data, error } = await supabase.functions.invoke('test-serpapi');
                      if (error) throw error;
                      
                      toast({
                        title: data.success ? "✅ Serper API Test" : "❌ Serper Test Failed",
                        description: data.message || data.error,
                        variant: data.success ? "default" : "destructive"
                      });
                      logger.debug('Serper Test:', data);
                    } catch (err: any) {
                      toast({
                        title: "❌ Test Failed",
                        description: err.message,
                        variant: "destructive"
                      });
                      console.error('Serper Test Error:', err);
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading}
                >
                  {loading ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <Sparkles className="mr-2 h-3 w-3" />}
                  Test Serper
                </Button>
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    setLoading(true);
                    try {
                      const { data, error } = await supabase.functions.invoke('test-deepseek');
                      if (error) throw error;
                      
                      toast({
                        title: data.success ? "✅ DeepSeek API Test" : "❌ DeepSeek Test Failed",
                        description: data.message || data.error,
                        variant: data.success ? "default" : "destructive"
                      });
                      logger.debug('DeepSeek Test:', data);
                    } catch (err: any) {
                      toast({
                        title: "❌ Test Failed",
                        description: err.message,
                        variant: "destructive"
                      });
                      console.error('DeepSeek Test Error:', err);
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading}
                >
                  {loading ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <Sparkles className="mr-2 h-3 w-3" />}
                  Test DeepSeek
                </Button>
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCompetitorTestDialog(true)}
                >
                  <Sparkles className="mr-2 h-3 w-3" />
                  Competitor AI Test
                </Button>
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={handleTestSalesflare}
                  disabled={loading}
                >
                  {loading ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <Send className="mr-2 h-3 w-3" />}
                  Test Salesflare
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <Tabs value={selectedTab} onValueChange={setSelectedTab} className="w-full">
          <div className="flex items-center justify-between gap-2 border-b pb-1.5">
            <TabsList className="flex-1 flex flex-nowrap md:grid md:grid-cols-8 gap-1 overflow-x-auto justify-start h-auto p-0.5 border-0">
              <TabsTrigger value="submissions" className="text-[11px] md:text-xs whitespace-nowrap flex-shrink-0 py-1 px-2">
                {isMobile ? `Zgłoszenia (${submissions.length + quizSubmissions.length})` : `Wszystkie zgłoszenia (${submissions.length + quizSubmissions.length})`}
              </TabsTrigger>
              <TabsTrigger value="automated-testing" className="text-[11px] md:text-xs whitespace-nowrap flex-shrink-0 py-1 px-2">
                🧪 Tests
              </TabsTrigger>
              <TabsTrigger value="headlines" className="text-[11px] md:text-xs whitespace-nowrap flex-shrink-0 py-1 px-2">
                A/B Headlines
              </TabsTrigger>
              <TabsTrigger value="linkedin-searcher" className="text-[11px] md:text-xs whitespace-nowrap flex-shrink-0 py-1 px-2">
                LinkedIn
              </TabsTrigger>
              <TabsTrigger value="competition-search" className="text-[11px] md:text-xs whitespace-nowrap flex-shrink-0 py-1 px-2">
                Competition
              </TabsTrigger>
              <TabsTrigger value="pageleads" className="text-[11px] md:text-xs whitespace-nowrap flex-shrink-0 py-1 px-2">
                📦 Pageleads
              </TabsTrigger>
              <TabsTrigger value="reports" className="text-[11px] md:text-xs whitespace-nowrap flex-shrink-0 py-1 px-2">
                Raporty
              </TabsTrigger>
              <TabsTrigger value="files" className="text-[11px] md:text-xs whitespace-nowrap flex-shrink-0 py-1 px-2">
                📄 Landing CMS
              </TabsTrigger>
            </TabsList>
            
            {selectedTab === 'submissions' && (
              <div className="flex items-center gap-2">
                <Select value={sourceProjectFilter} onValueChange={setSourceProjectFilter}>
                  <SelectTrigger className="w-[140px] h-7 text-[10px]">
                    <SelectValue placeholder="Źródło" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all" className="text-xs">Wszystkie źródła</SelectItem>
                    <SelectItem value="lifactor" className="text-xs">LifActor</SelectItem>
                    <SelectItem value="pageleads" className="text-xs">Pageleads</SelectItem>
                    <SelectItem value="meta-ads" className="text-xs">Meta Ads</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-muted/50">
                  <Label htmlFor="test-data-toggle" className="text-[10px] whitespace-nowrap cursor-pointer">
                    Test Data
                  </Label>
                  <Switch 
                    id="test-data-toggle"
                    checked={showTestData}
                    onCheckedChange={setShowTestData}
                  />
                </div>
              </div>
            )}
          </div>
          
          {showTestData && selectedTab === 'submissions' && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 p-3 mb-4 mt-4">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                <strong>Test Data Visible:</strong> Currently showing test submissions alongside real data.
              </p>
            </div>
          )}

          <TabsContent value="submissions">
            <Card>
              <CardHeader className="py-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <CardTitle className="text-base md:text-lg">Wszystkie Zgłoszenia (Quizy + Leady)</CardTitle>
                  {selectedSubmissions.size > 0 && (
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => setBulkDeleteOpen(true)}
                      className="h-7"
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1" />
                      Usuń zaznaczone ({selectedSubmissions.size})
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-3">
                {enrichmentQueue.length > 0 && (
                  <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                      <span className="font-semibold text-blue-900 dark:text-blue-100">
                        Kolejka wzbogacania: {enrichmentQueue.length} {enrichmentQueue.length === 1 ? 'zadanie' : 'zadań'}
                      </span>
                    </div>
                    
                    {currentlyEnriching && (
                      <div className="text-sm text-blue-700 dark:text-blue-300 mb-2">
                        Aktualnie przetwarzane: {
                          submissions.find(s => s.id === currentlyEnriching)?.name || 'Unknown'
                        }
                      </div>
                    )}
                    
                    <div className="mt-2 space-y-1">
                      {enrichmentQueue.slice(0, 3).map(item => {
                        const lead = submissions.find(s => s.id === item.leadId);
                        const progress = enrichmentProgress[item.leadId];
                        return (
                          <div key={item.leadId} className="text-xs flex items-center gap-2">
                            <Badge variant={
                              progress?.status === 'processing' ? 'default' :
                              progress?.status === 'completed' ? 'default' :
                              progress?.status === 'error' ? 'destructive' : 'secondary'
                            }>
                              {progress?.status === 'processing' ? 'Przetwarzanie' :
                               progress?.status === 'completed' ? 'Zakończone' :
                               progress?.status === 'error' ? 'Błąd' : 'W kolejce'}
                            </Badge>
                            <span className="text-blue-900 dark:text-blue-100">{lead?.name || item.leadId}</span>
                          </div>
                        );
                      })}
                      {enrichmentQueue.length > 3 && (
                        <div className="text-xs text-blue-700 dark:text-blue-300">
                          ... i {enrichmentQueue.length - 3} więcej
                        </div>
                      )}
                    </div>
                  </div>
                )}
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : submissions.length === 0 && quizSubmissions.length === 0 && socialSellingSubmissions.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                Brak zgłoszeń
              </p>
            ) : isMobile ? (
              <div className="space-y-4">
                {unifiedSubmissions.map((item) => {
                  if (item.type === 'quiz') {
                    const quiz = item.data as QuizSubmission;
                    const isTestRecord = quiz.session_id?.startsWith('test-');
                    const submissionType = quiz.converted_to_lead_id ? 'quiz-with-lead' : 
                                          quiz.report_download_email ? 'quiz-with-email' : 'quiz-only';
                    
                    return (
                      <MobileQuizCard
                        key={quiz.id}
                        quiz={quiz}
                        onEnrich={() => handleEnrichQuiz(quiz)}
                        onPushToCRM={() => handlePushQuizToSalesflare(quiz)}
                        onDelete={() => handleDeleteQuiz(quiz.id)}
                        onViewAnswers={() => {
                          setSelectedQuizSubmission(quiz);
                          setShowQuizDialog(true);
                        }}
                        onViewEnriched={quiz.enriched_data ? () => handleViewQuizEnrichedData(quiz) : undefined}
                        onViewCompetitorAnalysis={quiz.competitor_analysis ? () => {
                          setSelectedQuizSubmission(quiz);
                          setShowEnrichDialog(true);
                        } : undefined}
                        onGeneratePDF={() => handleGeneratePDF(quiz)}
                        isEnriching={enrichingId === quiz.id}
                        isPushing={pushingToSalesflareId === quiz.id}
                        getBandColor={getBandColor}
                        getBandLabel={getBandLabel}
                        submissionType={submissionType}
                        isTestRecord={isTestRecord}
                      />
                    );
                  } else if (item.type === 'lead') {
                    const submission = item.data as LeadSubmission;
                    return (
                      <MobileLeadCard
                        key={submission.id}
                        lead={{
                          id: submission.id,
                          name: submission.name,
                          email: submission.email,
                          phone: submission.phone,
                          company: submission.enriched_data?.company_name || submission.company || getDomainFromQuizAnswers(submission) || '-',
                          position: submission.position || '-',
                          linkedin_url: submission.enriched_data?.linkedin_url || submission.enriched_data?.alternative_sources?.linkedin_company || null,
                          score: submission.quiz_score || 0,
                          created_at: submission.created_at,
                          enriched_data: submission.enriched_data,
                          push_to_crm_at: submission.pushed_to_crm_at,
                          report_token: submission.report_token,
                          report_password: submission.report_password,
                          source_project: submission.source_project,
                        }}
                        onEnrich={() => handleEnrich(submission)}
                        onPushToCRM={() => handlePushToCRM(submission.id)}
                        onDelete={() => setDeleteId(submission.id)}
                        onViewEnriched={() => handleViewEnrichedData(submission)}
                        onViewQuiz={() => handleViewQuizAnswers(submission)}
                        onGeneratePDF={submission.quiz_answers ? () => handleGeneratePDF(submission) : undefined}
                        isEnriching={enrichingId === submission.id}
                        isPushing={pushingToSalesflareId === submission.id}
                        formatPhoneNumber={formatPhoneNumber}
                        getBadgeColor={(score) => getBandColor(submission.quiz_band)}
                        getBadgeLabel={(score) => getBandLabel(submission.quiz_band)}
                      />
                    );
                  } else if (item.type === 'ssa') {
                    const ssa = item.data as SocialSellingSubmission;
                    return (
                      <MobileSocialSellingCard
                        key={ssa.id}
                        submission={ssa}
                        onStartAudit={() => handleStartAudit(ssa)}
                        onViewReport={() => handleViewReport(ssa)}
                        onDelete={() => handleDeleteSocialSelling(ssa.id)}
                        onRetryAudit={() => handleRetryAudit(ssa.id)}
                        formatPhoneNumber={formatPhoneNumber}
                      />
                    );
                  } else {
                    return null;
                  }
                })}
              </div>
            ) : (
              <>
                {/* Top scrollbar - invisible table structure to match width */}
                <div 
                  ref={topScrollRef}
                  className="overflow-x-auto overflow-y-hidden mb-2 -mx-2 md:mx-0"
                  onScroll={(e) => {
                    if (tableScrollRef.current) {
                      tableScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
                    }
                  }}
                >
                  <div style={{ width: '1600px', height: '1px' }} />
                </div>

                {/* Table with bottom scrollbar */}
                <div 
                  ref={tableScrollRef}
                  className="overflow-x-auto -mx-2 md:mx-0"
                  onScroll={(e) => {
                    if (topScrollRef.current) {
                      topScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
                    }
                  }}
                >
                  <TooltipProvider delayDuration={200}>
                  <Table className="min-w-[1600px]">
                  <TableHeader>
                    <TableRow className="text-[11px]">
                      <TableHead className="py-1 px-1.5 w-8">
                        <input
                          type="checkbox"
                          className="cursor-pointer"
                          checked={unifiedSubmissions.length > 0 && unifiedSubmissions.every(it => selectedSubmissions.has(`${it.type}:${(it.data as any).id}`))}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedSubmissions(new Set(unifiedSubmissions.map(it => `${it.type}:${(it.data as any).id}`)));
                            } else {
                              setSelectedSubmissions(new Set());
                            }
                          }}
                        />
                      </TableHead>
                      <TableHead className="py-1 px-1.5">Typ</TableHead>
                      <TableHead className="py-1 px-1.5">Data</TableHead>
                      <TableHead className="py-1 px-1.5">Imię / Email / Domena</TableHead>
                      <TableHead className="py-1 px-1.5">Stanowisko</TableHead>
                      <TableHead className="py-1 px-1.5">Telefon</TableHead>
                      <TableHead className="py-1 px-1.5">Firma</TableHead>
                      <TableHead className="py-1 px-1.5">LinkedIn</TableHead>
                      <TableHead className="py-1 px-1.5">Wynik</TableHead>
                      <TableHead className="py-1 px-1.5">Poziom</TableHead>
                      <TableHead className="py-1 px-1.5">Status CRM</TableHead>
                      <TableHead className="py-1 px-1.5">Raport</TableHead>
                      <TableHead className="py-1 px-1.5">Wyśw.</TableHead>
                      <TableHead className="py-1 px-1.5">Dane</TableHead>
                      <TableHead className="py-1 px-1.5 text-right">Akcje</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {/* Render all submissions sorted by date */}
                    {unifiedSubmissions.map((item) => {
                      if (item.type === 'quiz') {
                        const quiz = item.data as QuizSubmission;
                        const isTestRecord = quiz.session_id?.startsWith('test-');
                        const submissionType = quiz.converted_to_lead_id ? 'quiz-with-lead' : 
                                              quiz.report_download_email ? 'quiz-with-email' : 'quiz-only';
                      
                      const isPushedToCRM = !!quiz.pushed_to_crm_at;
                      return (
                        <TableRow 
                          key={quiz.id}
                          className={`${isTestRecord ? 'bg-purple-50/50 dark:bg-purple-950/20 border-l-2 border-l-purple-500' : ''} ${isPushedToCRM ? 'bg-red-50/60 dark:bg-red-950/30 line-through opacity-60' : ''} text-[11px]`}
                         >
                           <TableCell className="py-1 px-1.5 w-8">
                             <input
                               type="checkbox"
                               className="cursor-pointer"
                               checked={selectedSubmissions.has(`quiz:${quiz.id}`)}
                               onChange={() => toggleSelectSubmission(`quiz:${quiz.id}`)}
                             />
                           </TableCell>
                           <TableCell className="py-1 px-1.5">
                            <div className="flex items-center gap-1">
                              {isTestRecord && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className="w-5 h-5 rounded bg-purple-100 dark:bg-purple-900 flex items-center justify-center cursor-default">
                                      <Beaker className="h-3 w-3 text-purple-600 dark:text-purple-300" />
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent>Test</TooltipContent>
                                </Tooltip>
                              )}
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className={`w-5 h-5 rounded flex items-center justify-center cursor-default ${
                                    submissionType === 'quiz-with-lead' ? 'bg-green-100 dark:bg-green-900' :
                                    submissionType === 'quiz-with-email' ? 'bg-blue-100 dark:bg-blue-900' : 'bg-gray-100 dark:bg-gray-800'
                                  }`}>
                                    {submissionType === 'quiz-with-lead' ? (
                                      <User className="h-3 w-3 text-green-600 dark:text-green-300" />
                                    ) : submissionType === 'quiz-with-email' ? (
                                      <Mail className="h-3 w-3 text-blue-600 dark:text-blue-300" />
                                    ) : (
                                      <ClipboardList className="h-3 w-3 text-gray-600 dark:text-gray-300" />
                                    )}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  {submissionType === 'quiz-with-lead' ? 'Quiz + Lead' :
                                   submissionType === 'quiz-with-email' ? 'Quiz + Email' : 'Quiz'}
                                </TooltipContent>
                              </Tooltip>
                            </div>
                          </TableCell>
                          <TableCell className="py-1 px-1.5 whitespace-nowrap text-[11px]">
                            {formatDate(quiz.created_at)}
                          </TableCell>
                          <TableCell className="py-1 px-1.5">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex flex-col gap-0 max-w-[180px] cursor-default">
                                  {quiz.lead_submission?.name && (
                                    <span className="font-medium text-[11px] truncate">{quiz.lead_submission.name}</span>
                                  )}
                                  {(quiz.lead_submission?.email || quiz.report_download_email) && (
                                    <span className="text-[9px] text-muted-foreground truncate">
                                      {quiz.lead_submission?.email || quiz.report_download_email}
                                    </span>
                                  )}
                                  {!quiz.lead_submission?.name && !quiz.lead_submission?.email && !quiz.report_download_email && (
                                    <span className="text-[11px] truncate">{quiz.domain_input || '-'}</span>
                                  )}
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>
                                {quiz.lead_submission?.name && <p>{quiz.lead_submission.name}</p>}
                                {(quiz.lead_submission?.email || quiz.report_download_email) && <p className="text-xs">{quiz.lead_submission?.email || quiz.report_download_email}</p>}
                                {!quiz.lead_submission?.name && !quiz.lead_submission?.email && !quiz.report_download_email && <p>{quiz.domain_input || '-'}</p>}
                              </TooltipContent>
                            </Tooltip>
                          </TableCell>
                          <TableCell className="py-1 px-1.5 text-[11px] max-w-[100px]">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="truncate block cursor-default">{quiz.lead_submission?.position || '-'}</span>
                              </TooltipTrigger>
                              <TooltipContent><p>{quiz.lead_submission?.position || '-'}</p></TooltipContent>
                            </Tooltip>
                          </TableCell>
                          <TableCell className="py-1 px-1.5 text-[11px] whitespace-nowrap">
                            {quiz.lead_submission?.phone ? formatPhoneNumber(quiz.lead_submission.phone) : '-'}
                          </TableCell>
                          <TableCell className="py-1 px-1.5 text-[11px] max-w-[130px]">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="truncate block cursor-default">{quiz.domain_input || '-'}</span>
                              </TooltipTrigger>
                              <TooltipContent><p>{quiz.domain_input || '-'}</p></TooltipContent>
                            </Tooltip>
                          </TableCell>
                          <TableCell className="py-1 px-1.5">
                            {quiz.quiz_answers?.found_linkedin_profile?.linkedinUrl ? (
                              <a 
                                href={quiz.quiz_answers.found_linkedin_profile.linkedinUrl}
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-primary hover:underline text-[10px]"
                              >
                                Link
                              </a>
                            ) : <span className="text-[11px]">-</span>}
                          </TableCell>
                          <TableCell className="py-1 px-1.5 text-[11px] whitespace-nowrap">
                            {quiz.quiz_score ? `${quiz.quiz_score}/10` : '-'}
                          </TableCell>
                          <TableCell className="py-1 px-1.5">
                            <Badge variant={getBandColor(quiz.quiz_band)} className="text-[9px] px-1 py-0">
                              {getBandLabel(quiz.quiz_band)}
                            </Badge>
                          </TableCell>
                          <TableCell className="py-1 px-1.5">
                            <div className="flex flex-col gap-0.5 min-w-[110px]">
                              <Select
                                value={quiz.status || 'new'}
                                onValueChange={(value) => handleUpdateQuizStatus(quiz.id, value)}
                              >
                                <SelectTrigger className="h-5 text-[9px]">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="new">🆕 Nowy</SelectItem>
                                  <SelectItem value="email_sent">📧 Email wysłany</SelectItem>
                                  <SelectItem value="contacted">📞 Kontakt tel.</SelectItem>
                                  <SelectItem value="meeting_scheduled">📅 Spotkanie</SelectItem>
                                  <SelectItem value="closed">✅ Zamknięty</SelectItem>
                                </SelectContent>
                              </Select>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    onClick={() => setNotesModal({ isOpen: true, type: 'quiz', id: quiz.id, notes: quiz.notes || '' })}
                                    className="h-5 w-full text-left text-[9px] px-1.5 py-0.5 bg-muted/50 hover:bg-muted rounded truncate flex items-center gap-1"
                                  >
                                    <StickyNote className="h-2.5 w-2.5 flex-shrink-0 text-muted-foreground" />
                                    <span className="truncate">{quiz.notes || 'Notatki...'}</span>
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-[300px]">
                                  <p className="text-xs whitespace-pre-wrap">{quiz.notes || 'Brak notatek'}</p>
                                </TooltipContent>
                              </Tooltip>
                              <div className="flex gap-0.5">
                                {quiz.enriched_at && (
                                  <Badge variant="outline" className="text-[8px] px-1 py-0">✓ Enr</Badge>
                                )}
                                {quiz.pushed_to_crm_at && (
                                  <Badge variant="outline" className="text-[8px] px-1 py-0">✓ CRM</Badge>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="py-2 px-2">
                            {quiz.report_token && quiz.report_password ? (
                              <div className="flex flex-col gap-1 min-w-[140px]">
                                <div className="flex items-center gap-1">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-6 px-2 text-[10px]"
                                    onClick={() => {
                                      const urlSlug = quiz.slug || quiz.report_token;
                                      const url = `${window.location.origin}/linkedin-entry-score/${urlSlug}`;
                                      navigator.clipboard.writeText(url);
                                      toast({
                                        title: "Skopiowano!",
                                        description: "Link do raportu został skopiowany"
                                      });
                                    }}
                                    title="Kopiuj link"
                                  >
                                    <LinkIcon className="h-2.5 w-2.5 mr-0.5" />
                                    Link
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 w-6 p-0"
                                    onClick={() => {
                                      const urlSlug = quiz.slug || quiz.report_token;
                                      window.open(`/linkedin-entry-score/${urlSlug}`, '_blank');
                                    }}
                                    title="Otwórz w nowej karcie"
                                  >
                                    <ExternalLink className="h-2.5 w-2.5" />
                                  </Button>
                                </div>
                                <div className="flex items-center gap-1">
                                  <code className="text-[10px] bg-muted px-1.5 py-0.5 rounded font-mono">
                                    {quiz.report_password}
                                  </code>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-5 w-5 p-0"
                                    onClick={() => {
                                      navigator.clipboard.writeText(quiz.report_password || '');
                                      toast({
                                        title: "Skopiowano!",
                                        description: "Hasło zostało skopiowane"
                                      });
                                    }}
                                    title="Kopiuj hasło"
                                  >
                                    <Copy className="h-2.5 w-2.5" />
                                  </Button>
                                </div>
                                {quiz.slug && (
                                  <div className="text-[9px] text-muted-foreground truncate">
                                    /{quiz.slug}
                                  </div>
                            )}
                              </div>
                            ) : (
                              <span className="text-[9px] text-muted-foreground">Brak</span>
                            )}
                          </TableCell>
                          <TableCell className="py-1 px-1.5 text-center">
                            <Badge variant="secondary" className="text-[9px] px-1 py-0">
                              {reportViewCounts[quiz.report_token || ''] || 0}
                            </Badge>
                          </TableCell>
                          <TableCell className="py-1 px-1.5">
                            <div className="flex flex-col gap-0 text-[9px]">
                              <span className={quiz.domain_input ? "text-green-600" : "text-muted-foreground"}>
                                D:{quiz.domain_input ? "✓" : "✗"}
                              </span>
                              <span className={quiz.competitor_analysis ? "text-green-600" : "text-muted-foreground"}>
                                K:{quiz.competitor_analysis ? "✓" : "✗"}
                              </span>
                              <span className={quiz.quiz_answers?.found_linkedin_profile ? "text-green-600" : "text-muted-foreground"}>
                                L:{quiz.quiz_answers?.found_linkedin_profile ? "✓" : "✗"}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="py-1 px-1.5 text-right">
                            <div className="flex gap-0.5 justify-end flex-wrap">
                              {quiz.quiz_answers && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="secondary"
                                    className="h-5 px-1.5 text-[9px]"
                                    onClick={() => {
                                      setSelectedQuizSubmission(quiz);
                                      setShowQuizDialog(true);
                                    }}
                                  >
                                    Odp
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="default"
                                    className="h-5 w-5 p-0"
                                    onClick={() => handleGeneratePDF(quiz)}
                                    title="Pobierz raport PDF"
                                  >
                                    <Download className="h-2.5 w-2.5" />
                                  </Button>
                                </>
                              )}
                              {quiz.competitor_analysis && (
                                <Button
                                  size="sm"
                                  variant="default"
                                  className="h-5 px-1.5 text-[9px]"
                                  onClick={() => {
                                    setSelectedQuizSubmission(quiz);
                                    setShowEnrichDialog(true);
                                  }}
                                >
                                  AI
                                </Button>
                              )}
                              {quiz.enriched_data && (
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  className="h-5 w-5 p-0"
                                  onClick={() => handleViewQuizEnrichedData(quiz)}
                                  title="Zobacz wzbogacone dane"
                                >
                                  <Eye className="h-2.5 w-2.5" />
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-5 w-5 p-0"
                                onClick={() => handleEnrichQuiz(quiz)}
                                disabled={enrichingId === quiz.id}
                                title="Enrich"
                              >
                                {enrichingId === quiz.id ? (
                                  <Loader2 className="h-2.5 w-2.5 animate-spin" />
                                ) : (
                                  <Sparkles className="h-2.5 w-2.5" />
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-5 w-5 p-0"
                                onClick={() => handlePushQuizToSalesflare(quiz)}
                                disabled={pushingToSalesflareId === quiz.id}
                                title="Push to CRM"
                              >
                                {pushingToSalesflareId === quiz.id ? (
                                  <Loader2 className="h-2.5 w-2.5 animate-spin" />
                                ) : (
                                  <Send className="h-2.5 w-2.5" />
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                className="h-5 w-5 p-0"
                                onClick={() => handleDeleteQuiz(quiz.id)}
                              >
                                <Trash2 className="h-2.5 w-2.5" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                      } else if (item.type === 'lead') {
                        const submission = item.data as LeadSubmission;
                        const isTestRecord = submission.email?.includes('@test') || 
                                            (submission.quiz_submission_id && quizSubmissions.find(q => q.id === submission.quiz_submission_id && q.session_id?.startsWith('test-')));
                        const isPushedToCRM = !!submission.pushed_to_crm_at;
                        return (
                        <TableRow 
                          key={submission.id}
                          className={`${isTestRecord ? 'bg-purple-50/50 dark:bg-purple-950/20 border-l-2 border-l-purple-500' : ''} ${isPushedToCRM ? 'bg-red-50/60 dark:bg-red-950/30 line-through opacity-60' : ''} text-[11px]`}
                         >
                         <TableCell className="py-1 px-1.5 w-8">
                           <input
                             type="checkbox"
                             className="cursor-pointer"
                             checked={selectedSubmissions.has(`lead:${submission.id}`)}
                             onChange={() => toggleSelectSubmission(`lead:${submission.id}`)}
                           />
                         </TableCell>
                         <TableCell className="py-1 px-1.5">
                          <div className="flex items-center gap-1">
                            {isTestRecord && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="w-5 h-5 rounded bg-purple-100 dark:bg-purple-900 flex items-center justify-center cursor-default">
                                    <Beaker className="h-3 w-3 text-purple-600 dark:text-purple-300" />
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>Test</TooltipContent>
                              </Tooltip>
                            )}
                            {submission.source_project === 'meta-ads' && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="w-5 h-5 rounded bg-orange-100 dark:bg-orange-900 flex items-center justify-center cursor-default">
                                    <Facebook className="h-3 w-3 text-orange-600 dark:text-orange-300" />
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>Meta Ads</TooltipContent>
                              </Tooltip>
                            )}
                            {submission.source_project === 'pageleads' && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="w-5 h-5 rounded bg-blue-100 dark:bg-blue-900 flex items-center justify-center cursor-default">
                                    <Package className="h-3 w-3 text-blue-600 dark:text-blue-300" />
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>Pageleads</TooltipContent>
                              </Tooltip>
                            )}
                            {submission.source_project === 'lifactor-website' && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="w-5 h-5 rounded bg-emerald-100 dark:bg-emerald-900 flex items-center justify-center cursor-default">
                                    <Globe className="h-3 w-3 text-emerald-600 dark:text-emerald-300" />
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>main lifactor</TooltipContent>
                              </Tooltip>
                            )}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="w-5 h-5 rounded bg-green-100 dark:bg-green-900 flex items-center justify-center cursor-default">
                                  <User className="h-3 w-3 text-green-600 dark:text-green-300" />
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>Lead</TooltipContent>
                            </Tooltip>
                          </div>
                        </TableCell>
                        <TableCell className="py-1 px-1.5 whitespace-nowrap text-[11px]">
                          {formatDate(submission.created_at)}
                        </TableCell>
                        <TableCell className="py-1 px-1.5 text-[11px] max-w-[150px]">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="truncate block font-medium cursor-default">{submission.name}</span>
                            </TooltipTrigger>
                            <TooltipContent><p>{submission.name}</p></TooltipContent>
                          </Tooltip>
                        </TableCell>
                        <TableCell className="py-1 px-1.5 text-[11px] max-w-[100px]">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="truncate block cursor-default">{submission.position || '-'}</span>
                            </TooltipTrigger>
                            <TooltipContent><p>{submission.position || '-'}</p></TooltipContent>
                          </Tooltip>
                        </TableCell>
                        <TableCell className="py-1 px-1.5 text-[11px] max-w-[140px]">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="truncate block cursor-default">{submission.email}</span>
                            </TooltipTrigger>
                            <TooltipContent><p>{submission.email}</p></TooltipContent>
                          </Tooltip>
                        </TableCell>
                        <TableCell className="py-1 px-1.5 text-[11px] whitespace-nowrap">{formatPhoneNumber(submission.phone)}</TableCell>
                        <TableCell className="py-1 px-1.5 text-[11px] max-w-[130px]">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex flex-col gap-0 cursor-default">
                                <span className="truncate">{submission.enriched_data?.company_name || submission.company || '-'}</span>
                                {getDomainFromQuizAnswers(submission) && (
                                  <span className="text-[9px] text-muted-foreground truncate">
                                    {getDomainFromQuizAnswers(submission)}
                                  </span>
                                )}
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{submission.enriched_data?.company_name || submission.company || '-'}</p>
                              {getDomainFromQuizAnswers(submission) && <p className="text-xs text-muted-foreground">{getDomainFromQuizAnswers(submission)}</p>}
                            </TooltipContent>
                          </Tooltip>
                        </TableCell>
                        <TableCell className="py-1 px-1.5">
                          {submission.enriched_data?.linkedin_url || submission.enriched_data?.alternative_sources?.linkedin_company ? (
                            <a 
                              href={(submission.enriched_data.linkedin_url || submission.enriched_data.alternative_sources?.linkedin_company) as string}
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-primary hover:underline text-[10px]"
                            >
                              Link
                            </a>
                          ) : <span className="text-[11px]">-</span>}
                        </TableCell>
                        <TableCell className="py-1 px-1.5 text-[11px] whitespace-nowrap">
                          {submission.quiz_score ? `${submission.quiz_score}/10` : '-'}
                        </TableCell>
                        <TableCell className="py-1 px-1.5">
                          <Badge variant={getBandColor(submission.quiz_band)} className="text-[9px] px-1 py-0">
                            {getBandLabel(submission.quiz_band)}
                          </Badge>
                        </TableCell>
                        <TableCell className="py-1 px-1.5">
                          <div className="flex flex-col gap-0.5 min-w-[110px]">
                            <Select
                              value={submission.crm_status || 'new'}
                              onValueChange={(value) => handleUpdateLeadStatus(submission.id, value)}
                            >
                              <SelectTrigger className="h-5 text-[9px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="new">🆕 Nowy</SelectItem>
                                <SelectItem value="email_sent">📧 Email wysłany</SelectItem>
                                <SelectItem value="contacted">📞 Kontakt tel.</SelectItem>
                                <SelectItem value="meeting_scheduled">📅 Spotkanie</SelectItem>
                                <SelectItem value="closed">✅ Zamknięty</SelectItem>
                              </SelectContent>
                            </Select>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={() => setNotesModal({ isOpen: true, type: 'lead', id: submission.id, notes: submission.notes || '' })}
                                  className="h-5 w-full text-left text-[9px] px-1.5 py-0.5 bg-muted/50 hover:bg-muted rounded truncate flex items-center gap-1"
                                >
                                  <StickyNote className="h-2.5 w-2.5 flex-shrink-0 text-muted-foreground" />
                                  <span className="truncate">{submission.notes || 'Notatki...'}</span>
                                </button>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-[300px]">
                                <p className="text-xs whitespace-pre-wrap">{submission.notes || 'Brak notatek'}</p>
                              </TooltipContent>
                            </Tooltip>
                            <div className="flex gap-0.5">
                              {submission.enriched_at && (
                                <Badge variant="outline" className="text-[8px] px-1 py-0">✓ Enr</Badge>
                              )}
                              {submission.pushed_to_crm_at && (
                                <Badge variant="outline" className="text-[8px] px-1 py-0">✓ CRM</Badge>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="py-1 px-1.5 text-[11px] text-muted-foreground">-</TableCell>
                        <TableCell className="py-1 px-1.5">
                          {submission.report_token && submission.report_password ? (
                            <div className="flex flex-col gap-0.5 min-w-[110px]">
                              <div className="flex items-center gap-0.5">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-5 px-1.5 text-[9px]"
                                  onClick={() => {
                                    const url = `${window.location.origin}/linkedin-entry-score/${submission.report_token}`;
                                    navigator.clipboard.writeText(url);
                                    toast({
                                      title: "Skopiowano!",
                                      description: "Link do raportu został skopiowany"
                                    });
                                  }}
                                  title="Kopiuj link"
                                >
                                  <LinkIcon className="h-2 w-2 mr-0.5" />
                                  Link
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-5 w-5 p-0"
                                  onClick={() => {
                                    window.open(`/linkedin-entry-score/${submission.report_token}`, '_blank');
                                  }}
                                  title="Otwórz w nowej karcie"
                                >
                                  <ExternalLink className="h-2 w-2" />
                                </Button>
                              </div>
                              <div className="flex items-center gap-0.5">
                                <code className="text-[9px] bg-muted px-1 py-0 rounded font-mono">
                                  {submission.report_password}
                                </code>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-4 w-4 p-0"
                                  onClick={() => {
                                    navigator.clipboard.writeText(submission.report_password || '');
                                    toast({
                                      title: "Skopiowano!",
                                      description: "Hasło zostało skopiowane"
                                    });
                                   }}
                                   title="Kopiuj hasło"
                                 >
                                   <Copy className="h-2 w-2" />
                                 </Button>
                               </div>
                             </div>
                            ) : (
                              <span className="text-[9px] text-muted-foreground">Brak</span>
                            )}
                          </TableCell>
                          <TableCell className="py-1 px-1.5 text-center">
                            <Badge variant="secondary" className="text-[9px] px-1 py-0">
                              {reportViewCounts[submission.report_token || ''] || 0}
                            </Badge>
                          </TableCell>
                          <TableCell className="py-1 px-1.5 text-right">
                           <div className="flex gap-0.5 justify-end flex-wrap">
                            {submission.quiz_answers && (
                              <>
                                <Button size="sm" variant="secondary" className="h-5 px-1 text-[9px]" onClick={() => handleViewQuizAnswers(submission)}>Quiz</Button>
                                <Button size="sm" variant="default" className="h-5 w-5 p-0" onClick={() => handleGeneratePDF(submission)} title="PDF"><Download className="h-2.5 w-2.5" /></Button>
                              </>
                            )}
                            {submission.enriched_data && (
                              <Button size="sm" variant="default" className="h-5 w-5 p-0" onClick={() => handleViewEnrichedData(submission)} title="Zobacz"><Eye className="h-2.5 w-2.5" /></Button>
                            )}
                            <Button size="sm" variant="outline" className="h-5 w-5 p-0" onClick={() => handleEnrich(submission)} disabled={enrichingId === submission.id} title="Enrich">
                              {enrichingId === submission.id ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <Sparkles className="h-2.5 w-2.5" />}
                            </Button>
                            <Button size="sm" variant="outline" className="h-5 w-5 p-0" onClick={() => handlePushToCRM(submission.id)} disabled={pushingToSalesflareId === submission.id} title="CRM">
                              {pushingToSalesflareId === submission.id ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <Send className="h-2.5 w-2.5" />}
                            </Button>
                            <Button size="sm" variant="destructive" className="h-5 w-5 p-0" onClick={() => setDeleteId(submission.id)}>
                              <Trash2 className="h-2.5 w-2.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      );
                      } else if (item.type === 'ssa') {
                        const ssa = item.data as SocialSellingSubmission;
                        const hasLowConfidence = ssa.report_data?.meta?.confidenceLevels && 
                          Object.values(ssa.report_data.meta.confidenceLevels).some((c: any) => c === 'low' || c === 'very-low');
                        
                        const getSSAStatusBadge = (status: string) => {
                          switch (status) {
                            case "completed":
                              return <Badge className="bg-green-500 text-[10px] px-1 py-0">Ukończony</Badge>;
                            case "processing":
                              return <Badge className="bg-blue-500 text-[10px] px-1 py-0">W trakcie</Badge>;
                            case "failed":
                              return <Badge className="bg-red-500 text-[10px] px-1 py-0">Błąd</Badge>;
                            default:
                              return <Badge variant="outline" className="text-[10px] px-1 py-0">Oczekujący</Badge>;
                          }
                        };

                        return (
                          <TableRow key={ssa.id} className="text-xs bg-violet-50/30 dark:bg-violet-950/20 border-l-2 border-l-violet-500">
                            <TableCell className="py-2 px-2 w-8">
                              <input
                                type="checkbox"
                                className="cursor-pointer"
                                checked={selectedSubmissions.has(`ssa:${ssa.id}`)}
                                onChange={() => toggleSelectSubmission(`ssa:${ssa.id}`)}
                              />
                            </TableCell>
                            <TableCell className="py-2 px-2">
                              <Badge variant="outline" className="bg-violet-100 dark:bg-violet-900 text-violet-700 dark:text-violet-300 border-violet-300 dark:border-violet-700 text-[10px] px-1 py-0">
                                🎯 SSA
                              </Badge>
                            </TableCell>
                            <TableCell className="py-2 px-2 whitespace-nowrap text-xs">
                              {formatDate(ssa.created_at)}
                            </TableCell>
                            <TableCell className="py-2 px-2">
                              <div className="flex flex-col gap-0.5 max-w-[200px]">
                                <div className="font-medium text-xs truncate">{ssa.user_domain}</div>
                                <div className="text-[10px] text-muted-foreground truncate">{ssa.email}</div>
                              </div>
                            </TableCell>
                            <TableCell className="py-2 px-2 text-xs text-muted-foreground">-</TableCell>
                            <TableCell className="py-2 px-2 text-xs whitespace-nowrap">
                              {formatPhoneNumber(ssa.phone)}
                            </TableCell>
                            <TableCell className="py-2 px-2 text-xs max-w-[150px]">
                              {ssa.competitor_domains && ssa.competitor_domains.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {ssa.competitor_domains.slice(0, 2).map((comp, idx) => (
                                    <Badge key={idx} variant="outline" className="text-[10px] px-1 py-0">{comp}</Badge>
                                  ))}
                                  {ssa.competitor_domains.length > 2 && (
                                    <Badge variant="outline" className="text-[10px] px-1 py-0">+{ssa.competitor_domains.length - 2}</Badge>
                                  )}
                                </div>
                              ) : '-'}
                            </TableCell>
                            <TableCell className="py-2 px-2 text-xs">-</TableCell>
                            <TableCell className="py-2 px-2 text-xs">
                              {ssa.report_data?.overallScore ? `${Math.round(ssa.report_data.overallScore)}/100` : '-'}
                            </TableCell>
                            <TableCell className="py-2 px-2">
                              {getSSAStatusBadge(ssa.status)}
                            </TableCell>
                            <TableCell className="py-2 px-2 text-xs text-muted-foreground">-</TableCell>
                            <TableCell className="py-2 px-2">
                              {ssa.status === 'completed' && ssa.report_data ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-6 px-2 text-[10px]"
                                  onClick={() => handleViewReport(ssa)}
                                >
                                  <FileText className="h-2.5 w-2.5 mr-0.5" />
                                  Raport
                                </Button>
                              ) : '-'}
                            </TableCell>
                            <TableCell className="py-2 px-2 text-xs text-muted-foreground">-</TableCell>
                            <TableCell className="py-2 px-2 text-xs text-muted-foreground">-</TableCell>
                            <TableCell className="py-2 px-2 text-right">
                              <div className="flex gap-1 justify-end flex-wrap">
                                {ssa.status === 'pending' && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleStartAudit(ssa)}
                                  >
                                    <Play className="h-4 w-4 mr-1" />
                                    Start
                                  </Button>
                                )}
                                {ssa.status === 'completed' && hasLowConfidence && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleRetryAudit(ssa.id)}
                                    title="Ponów audyt z powodu niskiej pewności danych"
                                  >
                                    <RefreshCw className="h-3 w-3 mr-1" />
                                    Retry
                                  </Button>
                                )}
                                {ssa.status === 'processing' && (
                                  <AuditProgressIndicator status={ssa.status} createdAt={ssa.created_at} />
                                )}
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => handleDeleteSocialSelling(ssa.id)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      } else {
                        return null;
                      }
                    })}
                  </TableBody>
                </Table>
                </TooltipProvider>
                </div>
              </>
            )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="social-selling">
            {activeAuditId ? (
              <SocialSellingAuditProcess
                submissionId={activeAuditId}
                onComplete={handleAuditComplete}
                onCancel={() => setActiveAuditId(null)}
              />
            ) : (
              <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg md:text-xl">Audyt Social Selling</CardTitle>
                      {socialSellingSubmissions.length > 0 && (
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={handleSelfTest}
                          >
                            <Play className="h-4 w-4 mr-2" />
                            Self Test
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={handleFixStuckAudits}
                            title="Ustaw status 'completed' dla audytów z report_data"
                          >
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Napraw zawieszone
                          </Button>
                        </div>
                      )}
                    </div>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin" />
                    </div>
                  ) : socialSellingSubmissions.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">
                      Brak zgłoszeń
                    </p>
                  ) : isMobile ? (
                    <div className="space-y-4">
                      {socialSellingSubmissions.map((submission) => (
                        <MobileSocialSellingCard
                          key={submission.id}
                          submission={submission}
                          onStartAudit={() => handleStartAudit(submission)}
                          onViewReport={() => handleViewReport(submission)}
                          onDelete={() => handleDeleteSocialSelling(submission.id)}
                          onRetryAudit={() => handleRetryAudit(submission.id)}
                          formatPhoneNumber={formatPhoneNumber}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Data</TableHead>
                            <TableHead>Domena</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Telefon</TableHead>
                            <TableHead>Status/Przebieg</TableHead>
                            <TableHead>Wyniki</TableHead>
                            <TableHead>Konkurenci</TableHead>
                            <TableHead className="text-right">Akcje</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {socialSellingSubmissions.map((submission) => (
                            <TableRow key={submission.id}>
                              <TableCell className="whitespace-nowrap">
                                {formatDate(submission.created_at)}
                              </TableCell>
                              <TableCell>
                                <a 
                                  href={`https://${submission.user_domain}`} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-primary hover:underline"
                                >
                                  {submission.user_domain}
                                </a>
                              </TableCell>
                              <TableCell>{submission.email}</TableCell>
                              <TableCell>{formatPhoneNumber(submission.phone)}</TableCell>
                              <TableCell>
                                {submission.status === 'processing' && !submission.report_data ? (
                                  <AuditProgressIndicator 
                                    status={submission.status}
                                    createdAt={submission.created_at}
                                    compact
                                  />
                                ) : submission.report_data ? (
                                  <Badge variant="default" className="bg-green-500">✓ Ukończony</Badge>
                                ) : submission.status === 'failed' ? (
                                  <Badge variant="destructive">Błąd</Badge>
                                ) : (
                                  <Badge variant="outline">Oczekujący</Badge>
                                )}
                              </TableCell>
                              <TableCell>
                                {submission.report_data ? (
                                  <div className="flex items-center gap-2">
                                    {submission.status === 'completed' ? (
                                      <>
                                        {submission.report_data.warnings && submission.report_data.warnings.length > 0 && (
                                          <div className="mb-4 space-y-2">
                                            <p className="text-sm font-medium">Ostrzeżenia:</p>
                                            {submission.report_data.warnings.map((warning: any, idx: number) => (
                                              <div key={idx} className={`text-sm p-2 rounded ${
                                                warning.level === 'warning' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'
                                              }`}>
                                                <strong>{warning.level === 'warning' ? '⚠️' : 'ℹ️'}</strong> {warning.message}
                                                {warning.impact && <div className="text-xs mt-1">{warning.impact}</div>}
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                        <AuditResultsSummary reportData={submission.report_data} />
                                      </>
                                    ) : (
                                      <Button size="sm" variant="secondary" onClick={() => handleViewReport(submission)}>
                                        Zobacz raport
                                      </Button>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground text-sm">-</span>
                                )}
                              </TableCell>
                              <TableCell>
                                {submission.competitor_domains?.length ? (
                                  <div className="space-y-1">
                                    {submission.competitor_domains.map((domain, idx) => (
                                      <div key={idx} className="text-xs">
                                        <a 
                                          href={`https://${domain}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-primary hover:underline"
                                        >
                                          {domain}
                                        </a>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex gap-2 justify-end">
                                  {submission.status === 'pending' && (
                                    <Button
                                      size="sm"
                                      variant="default"
                                      onClick={() => handleStartAudit(submission)}
                                    >
                                      <Sparkles className="h-4 w-4 mr-1" />
                                      Start Audit
                                    </Button>
                                  )}
                                  {submission.status === 'completed' && submission.report_data && (
                                    <>
                                      <Button
                                        size="sm"
                                        variant="secondary"
                                      onClick={() => handleViewReport(submission)}
                                      >
                                        Zobacz raport
                                      </Button>
                                      {/* FAZA 5: Show retry button if recommended OR if low confidence */}
                                      {((submission.report_data.meta as any)?.retryRecommended || 
                                        (submission.report_data.meta?.confidenceLevels && 
                                         Object.values(submission.report_data.meta.confidenceLevels).some((c: any) => c === 'low' || c === 'very-low'))) && (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => handleRetryAudit(submission.id)}
                                          title={(submission.report_data.meta as any)?.retryReason || "Ponów audyt z powodu niskiej pewności danych"}
                                        >
                                          <RefreshCw className="h-3 w-3 mr-1" />
                                          {(submission.report_data.meta as any)?.retryRecommended ? 'Auto-Retry' : 'Retry'}
                                        </Button>
                                      )}
                                    </>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleDeleteSocialSelling(submission.id)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="linkedin-searcher">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg md:text-xl">LinkedIn Company Searcher</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      Wprowadź domeny firm (bez http://, www), aby znaleźć ich profile LinkedIn Company.
                    </p>
                    
                    {searchDomains.map((domain, index) => (
                      <div key={index} className="space-y-2">
                        <Label htmlFor={`domain-${index}`}>Domena {index + 1}</Label>
                        <div className="flex gap-2">
                          <Input
                            id={`domain-${index}`}
                            type="text"
                            placeholder="np. example.com"
                            value={domain}
                            onChange={(e) => handleDomainChange(index, e.target.value)}
                            disabled={searchingLinkedIn}
                            className="flex-1"
                          />
                          {searchDomains.length > 1 && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleRemoveDomain(index)}
                              disabled={searchingLinkedIn}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                    
                    <Button
                      variant="outline"
                      onClick={handleAddDomain}
                      disabled={searchingLinkedIn}
                      className="w-full"
                    >
                      Dodaj kolejną domenę
                    </Button>
                    
                    <div className="flex gap-2">
                      <Button
                        onClick={handleSearchLinkedIn}
                        disabled={searchingLinkedIn}
                        className="flex-1"
                      >
                        {searchingLinkedIn ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Szukam...
                          </>
                        ) : (
                          <>
                            <LinkIcon className="mr-2 h-4 w-4" />
                            Znajdź profile LinkedIn
                          </>
                        )}
                      </Button>
                      
                      {searchResults.length > 0 && (
                        <Button
                          variant="outline"
                          onClick={handleCopyAllLinks}
                        >
                          <Copy className="mr-2 h-4 w-4" />
                          Kopiuj wszystkie linki
                        </Button>
                      )}
                    </div>
                  </div>

                  {searchResults.length > 0 && (
                    <div className="space-y-2">
                      <h3 className="font-semibold">Wyniki wyszukiwania:</h3>
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Domena</TableHead>
                              <TableHead>LinkedIn URL</TableHead>
                              <TableHead>Score</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead>Akcje</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {searchResults.map((result, index) => (
                              <TableRow key={index}>
                                <TableCell className="font-medium">
                                  {result.domain}
                                </TableCell>
                                <TableCell>
                                  {result.linkedinUrl ? (
                                    <a
                                      href={result.linkedinUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-primary hover:underline flex items-center gap-1 text-xs md:text-sm"
                                    >
                                      {result.linkedinUrl.length > 40 
                                        ? result.linkedinUrl.substring(0, 40) + '...' 
                                        : result.linkedinUrl}
                                      <ExternalLink className="h-3 w-3" />
                                    </a>
                                  ) : (
                                    <span className="text-muted-foreground text-xs">-</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Badge variant={
                                    result.score >= 50 ? 'default' :
                                    result.score >= 20 ? 'secondary' :
                                    'outline'
                                  }>
                                    {result.score}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <Badge variant={
                                    result.status === 'found' ? 'default' :
                                    result.status === 'low_confidence' ? 'secondary' :
                                    result.status === 'error' ? 'destructive' :
                                    'outline'
                                  }>
                                    {result.status === 'found' ? '✓ Znaleziono' :
                                     result.status === 'low_confidence' ? '⚠ Niska pewność' :
                                     result.status === 'error' ? '✗ Błąd' :
                                     'Nie znaleziono'}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  {result.linkedinUrl && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        navigator.clipboard.writeText(result.linkedinUrl || '');
                                        toast({
                                          title: "✅ Skopiowano",
                                          description: "Link skopiowany do schowka"
                                        });
                                      }}
                                    >
                                      <Copy className="h-4 w-4" />
                                    </Button>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="b2b-test">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg md:text-xl">B2B Enrich Testing Module</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="company-url">LinkedIn Company URL</Label>
                    <Input
                      id="company-url"
                      type="text"
                      placeholder="https://www.linkedin.com/company/microsoft"
                      value={testCompanyUrl}
                      onChange={(e) => setTestCompanyUrl(e.target.value)}
                      disabled={testingCompanyUrl}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="search-name">Search Name (optional)</Label>
                    <Input
                      id="search-name"
                      type="text"
                      placeholder="e.g., Test Batch 1, Employee Search, etc."
                      value={searchName}
                      onChange={(e) => setSearchName(e.target.value)}
                      disabled={testingCompanyUrl}
                    />
                    <p className="text-xs text-muted-foreground">
                      Label for tracking this test (sent as search_name parameter to API)
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="company-names">Company Names (comma-separated, optional)</Label>
                    <Input
                      id="company-names"
                      type="text"
                      placeholder="e.g., Kopeć Zaborowski KKZ, Kancelaria Kopeć Zaborowski, Kopec Zaborowski"
                      value={companyNames}
                      onChange={(e) => setCompanyNames(e.target.value)}
                      disabled={testingCompanyUrl}
                    />
                    <p className="text-xs text-muted-foreground">
                      Multiple name variants to try when resolving the canonical company URL
                    </p>
                  </div>

                  <Button
                    onClick={handleTestCompanyUrl}
                    disabled={testingCompanyUrl || !testCompanyUrl.trim()}
                  >
                    {testingCompanyUrl ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Testing...
                      </>
                    ) : (
                      <>
                        <Sparkles className="mr-2 h-4 w-4" />
                        Test Company Employees API
                      </>
                    )}
                  </Button>

                  {companyTestResult && (
                    <Card className="mt-6">
                      <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                          {companyTestResult.success ? (
                            <>
                              <CheckCircle2 className="h-5 w-5 text-green-500" />
                              Test Results - Success
                            </>
                          ) : (
                            <>
                              <XCircle className="h-5 w-5 text-red-500" />
                              Test Results - Failed
                            </>
                          )}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <div className="text-sm">
                              <strong>Status:</strong>{' '}
                              <Badge variant={companyTestResult.success ? "default" : "destructive"}>
                                {companyTestResult.httpStatus || 'N/A'}
                              </Badge>
                            </div>
                            <div className="text-sm">
                              <strong>Response Time:</strong> {companyTestResult.responseTime}ms
                            </div>
                            <div className="text-sm">
                              <strong>URL Variants Generated:</strong> {companyTestResult.urlVariantsGenerated?.length || 0}
                            </div>
                            {companyTestResult.urlUsed && (
                              <div className="text-sm">
                                <strong>URL Used:</strong>
                                <code className="block mt-1 p-2 bg-muted rounded text-xs break-all">
                                  {companyTestResult.urlUsed}
                                </code>
                              </div>
                            )}
                            {companyTestResult.canonicalResolution && (
                              <div className="text-sm mt-2">
                                <Badge variant="secondary" className="bg-blue-100 dark:bg-blue-900">
                                  ℹ️ Resolved via canonical URL lookup
                                </Badge>
                              </div>
                            )}
                            {companyTestResult.canonicalUrlTried && (
                              <div className="text-sm mt-2">
                                <strong>Canonical URL Found:</strong>
                                <code className="block mt-1 p-2 bg-blue-50 dark:bg-blue-950 rounded text-xs break-all">
                                  {companyTestResult.canonicalUrlTried}
                                </code>
                              </div>
                            )}
                          </div>

                          {companyTestResult.success && companyTestResult.apiResponse && (
                            <div className="space-y-2">
                              <div className="text-sm">
                                <strong>Employees Found:</strong> {companyTestResult.apiResponse.data?.result?.length || 0}
                              </div>
                              {companyTestResult.apiResponse.search_info?.remaining_credit_balance !== undefined && (
                                <div className="text-sm">
                                  <strong>Remaining Credits:</strong> {companyTestResult.apiResponse.search_info.remaining_credit_balance}
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {companyTestResult.companySearchAttempts && companyTestResult.companySearchAttempts.length > 0 && (
                          <div className="space-y-2">
                            <strong className="text-sm">Company Search Attempts ({companyTestResult.companySearchAttempts.length}):</strong>
                            <div className="border rounded-lg overflow-hidden">
                              <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                  <thead className="bg-muted">
                                    <tr>
                                      <th className="text-left p-2 font-medium">#</th>
                                      <th className="text-left p-2 font-medium">Name Tried</th>
                                      <th className="text-left p-2 font-medium">Status</th>
                                      <th className="text-left p-2 font-medium">Time</th>
                                      <th className="text-left p-2 font-medium">Message</th>
                                      <th className="text-left p-2 font-medium">LinkedIn URL</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {companyTestResult.companySearchAttempts.map((attempt: any, idx: number) => (
                                      <tr 
                                        key={idx} 
                                        className={`border-t ${
                                          attempt.linkedinUrl
                                            ? 'bg-green-50 dark:bg-green-950'
                                            : 'hover:bg-muted/50'
                                        }`}
                                      >
                                        <td className="p-2">{idx + 1}</td>
                                        <td className="p-2">
                                          <code className="text-xs break-all">
                                            {attempt.nameTried}
                                          </code>
                                        </td>
                                        <td className="p-2">
                                          <Badge 
                                            variant={attempt.linkedinUrl ? "default" : "destructive"}
                                            className="text-xs"
                                          >
                                            {attempt.httpStatus}
                                          </Badge>
                                        </td>
                                        <td className="p-2">{attempt.responseTime}ms</td>
                                        <td className="p-2 text-xs">{attempt.message}</td>
                                        <td className="p-2 text-xs">
                                          {attempt.linkedinUrl ? (
                                            <code className="text-xs break-all text-green-600 dark:text-green-400">
                                              {attempt.linkedinUrl.length > 40 
                                                ? `${attempt.linkedinUrl.substring(0, 37)}...` 
                                                : attempt.linkedinUrl}
                                            </code>
                                          ) : (
                                            <span className="text-muted-foreground">-</span>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        )}

                        {companyTestResult.attempts && companyTestResult.attempts.length > 0 && (
                          <div className="space-y-2">
                            <strong className="text-sm">Employee Search Attempts ({companyTestResult.attempts.length}):</strong>
                            <div className="border rounded-lg overflow-hidden">
                              <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                  <thead className="bg-muted">
                                    <tr>
                                      <th className="text-left p-2 font-medium">#</th>
                                      <th className="text-left p-2 font-medium">Variant</th>
                                      <th className="text-left p-2 font-medium">Status</th>
                                      <th className="text-left p-2 font-medium">Time</th>
                                      <th className="text-left p-2 font-medium">Message</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {companyTestResult.attempts.map((attempt: any, idx: number) => (
                                      <tr 
                                        key={idx} 
                                        className={`border-t ${
                                          attempt.httpStatus === 200 && attempt.apiResponse?.status === true
                                            ? 'bg-green-50 dark:bg-green-950'
                                            : 'hover:bg-muted/50'
                                        }`}
                                      >
                                        <td className="p-2">{idx + 1}</td>
                                        <td className="p-2">
                                          <code className="text-xs break-all">
                                            {attempt.variant?.length > 50 
                                              ? `${attempt.variant.substring(0, 47)}...` 
                                              : attempt.variant}
                                          </code>
                                        </td>
                                        <td className="p-2">
                                          <Badge 
                                            variant={
                                              attempt.httpStatus === 200 && attempt.apiResponse?.status === true
                                                ? "default"
                                                : "destructive"
                                            }
                                            className="text-xs"
                                          >
                                            {attempt.httpStatus}
                                          </Badge>
                                        </td>
                                        <td className="p-2">{attempt.responseTime}ms</td>
                                        <td className="p-2 text-xs">
                                          {attempt.apiResponse?.errorCode && (
                                            <span className="text-destructive font-medium">
                                              [{attempt.apiResponse.errorCode}]{' '}
                                            </span>
                                          )}
                                          {attempt.message}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        )}

                        {companyTestResult.error && (
                          <div className="p-3 bg-destructive/10 border border-destructive/20 rounded">
                            <strong className="text-sm text-destructive">Error:</strong>
                            <p className="text-sm mt-1">{companyTestResult.error}</p>
                            {companyTestResult.apiResponse?.errorCode && (
                              <p className="text-xs mt-1 text-muted-foreground">
                                Error Code: {companyTestResult.apiResponse.errorCode}
                              </p>
                            )}
                          </div>
                        )}

                        <div className="space-y-2">
                          <strong className="text-sm">Raw API Response:</strong>
                          <pre className="p-4 bg-muted rounded-lg overflow-x-auto text-xs max-h-96 overflow-y-auto">
                            {JSON.stringify(companyTestResult.apiResponse || companyTestResult, null, 2)}
                          </pre>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="competition-search">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg md:text-xl">Competition Search Module</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Submission Form */}
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="competition-domain">Domena firmy</Label>
                      <Input
                        id="competition-domain"
                        type="text"
                        placeholder="np. example.com"
                        value={competitionDomain}
                        onChange={(e) => setCompetitionDomain(e.target.value)}
                        disabled={searchingCompetition}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && competitionDomain.trim()) {
                            handleSearchCompetition();
                          }
                        }}
                      />
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="fast-mode"
                        checked={fastMode}
                        onChange={(e) => setFastMode(e.target.checked)}
                        disabled={searchingCompetition}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      <Label htmlFor="fast-mode" className="text-sm font-normal">
                        Fast Mode (szybsze wyniki, bez szczegółowego rankingu)
                      </Label>
                    </div>
                    
                    <Button
                      onClick={handleSearchCompetition}
                      disabled={searchingCompetition || !competitionDomain.trim()}
                      className="w-full"
                    >
                      {searchingCompetition ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Dodawanie do kolejki...
                        </>
                      ) : (
                        <>
                          <Sparkles className="mr-2 h-4 w-4" />
                          Dodaj do kolejki
                        </>
                      )}
                    </Button>
                  </div>

                  {/* Excluded Domains Manager Button */}
                  <div className="flex justify-end mb-4">
                    <Button 
                      onClick={() => setExcludedDomainsModalOpen(true)}
                      variant="outline"
                      className="gap-2"
                    >
                      <Shield className="h-4 w-4" />
                      Zarządzaj domenami wykluczonymi
                    </Button>
                  </div>
                  
                  <ExcludedDomainsManager 
                    open={excludedDomainsModalOpen} 
                    onOpenChange={setExcludedDomainsModalOpen}
                  />
                  
                  {/* Queue Status */}
                  {competitionQueue.length > 0 && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-base font-semibold">Status kolejki</h3>
                        {queuePolling && (
                          <Badge variant="secondary" className="animate-pulse">
                            <RefreshCw className="mr-1 h-3 w-3" />
                            Auto-odświeżanie
                          </Badge>
                        )}
                      </div>
                      
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Domena</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead>Postęp</TableHead>
                              <TableHead>Utworzono</TableHead>
                              <TableHead className="text-right">Akcje</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {competitionQueue.map((job) => (
                              <TableRow key={job.id}>
                                <TableCell className="font-medium">{job.domain}</TableCell>
                                <TableCell>
                                  <Badge variant={
                                    job.status === 'completed' ? 'default' :
                                    job.status === 'processing' ? 'secondary' :
                                    job.status === 'failed' ? 'destructive' : 'outline'
                                  }>
                                    {job.status === 'queued' && <Clock className="mr-1 h-3 w-3" />}
                                    {job.status === 'processing' && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                                    {job.status === 'completed' && <CheckCircle2 className="mr-1 h-3 w-3" />}
                                    {job.status === 'failed' && <XCircle className="mr-1 h-3 w-3" />}
                                    {job.status === 'queued' ? 'W kolejce' :
                                     job.status === 'processing' ? 'Przetwarzanie' :
                                     job.status === 'completed' ? 'Zakończono' : 'Błąd'}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  {job.status === 'processing' || job.status === 'completed' ? (
                                    <div className="space-y-1">
                                      <div className="flex items-center gap-2">
                                        <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                                          <div 
                                            className="h-full bg-primary transition-all duration-300"
                                            style={{ width: `${job.progress_percent}%` }}
                                          />
                                        </div>
                                        <span className="text-xs text-muted-foreground">
                                          {job.progress_percent}%
                                        </span>
                                      </div>
                                      {job.progress && (
                                        <p className="text-xs text-muted-foreground">{job.progress}</p>
                                      )}
                                    </div>
                                  ) : job.status === 'failed' && job.error_message ? (
                                    <p className="text-xs text-destructive">{job.error_message}</p>
                                  ) : (
                                    <span className="text-xs text-muted-foreground">-</span>
                                  )}
                                </TableCell>
                                <TableCell className="text-xs text-muted-foreground">
                                  {new Date(job.created_at).toLocaleString('pl-PL', {
                                    dateStyle: 'short',
                                    timeStyle: 'short'
                                  })}
                                </TableCell>
                                <TableCell className="text-right">
                                  <div className="flex gap-1 justify-end">
                                    {job.status === 'completed' && job.result_data && (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleViewQueueResult(job)}
                                      >
                                        <Eye className="h-4 w-4" />
                                      </Button>
                                    )}
                                    {job.status === 'failed' && (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleRetryQueueJob(job.id)}
                                      >
                                        <RefreshCw className="h-4 w-4" />
                                      </Button>
                                    )}
                                    {(job.status === 'completed' || job.status === 'failed') && (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleDeleteQueueJob(job.id)}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}

                  {competitionResults && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-base font-semibold">Wyniki analizy</h3>
                        {competitionResults.competitors && competitionResults.competitors.length > 0 && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleCopyAllCompetitorDomains}
                          >
                            <Copy className="mr-2 h-4 w-4" />
                            Kopiuj wszystkie domeny
                          </Button>
                        )}
                      </div>
                      
                      <Card className="border-2">
                        <CardHeader className="pb-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <CardTitle className="text-base">{competitionResults.company_name}</CardTitle>
                              <p className="text-sm text-muted-foreground mt-1">{competitionResults.industry}</p>
                            </div>
                            <div className="flex gap-2">
                              <Badge variant={
                                competitionResults.confidence === 'high' ? 'default' :
                                competitionResults.confidence === 'medium' ? 'secondary' : 'outline'
                              }>
                                {competitionResults.confidence === 'high' ? '🎯 Wysokie' :
                                 competitionResults.confidence === 'medium' ? '⚠️ Średnie' : '❓ Niskie'} zaufanie
                              </Badge>
                              <Badge variant="outline">
                                {competitionResults.competitors.length} konkurentów
                              </Badge>
                            </div>
                          </div>
                        </CardHeader>
                      </Card>

                      {competitionResults.competitors.length > 0 ? (
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>#</TableHead>
                                <TableHead>Nazwa</TableHead>
                                <TableHead>Domena</TableHead>
                                <TableHead>Score</TableHead>
                                <TableHead>Poziom konkurencji</TableHead>
                                <TableHead className="text-right">Akcje</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {competitionResults.competitors.map((competitor, index) => (
                                <TableRow key={index}>
                                  <TableCell className="font-medium">{index + 1}</TableCell>
                                  <TableCell className="font-medium">{competitor.name}</TableCell>
                                  <TableCell>
                                    <a
                                      href={`https://${competitor.domain}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-primary hover:underline flex items-center gap-1"
                                    >
                                      {competitor.domain}
                                      <ExternalLink className="h-3 w-3" />
                                    </a>
                                  </TableCell>
                                  <TableCell>
                                    <Badge variant="outline">{competitor.score}</Badge>
                                  </TableCell>
                                  <TableCell>
                                    <Badge variant={
                                      competitor.competition_level === 'direct' ? 'destructive' :
                                      competitor.competition_level === 'indirect' ? 'default' : 'secondary'
                                    }>
                                      {competitor.competition_level === 'direct' ? '🔴 Bezpośredni' :
                                       competitor.competition_level === 'indirect' ? '🟠 Pośredni' : '🔵 Potencjalny'}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <div className="flex items-center justify-end gap-2">
                                      {competitorLinkedInUrls[competitor.domain]?.status === 'found' && competitorLinkedInUrls[competitor.domain]?.url ? (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => {
                                            const url = competitorLinkedInUrls[competitor.domain]?.url;
                                            if (url) {
                                              navigator.clipboard.writeText(url);
                                              toast({
                                                title: "Skopiowano!",
                                                description: "Link LinkedIn został skopiowany"
                                              });
                                            }
                                          }}
                                        >
                                          <LinkIcon className="h-3 w-3 mr-1" />
                                          Kopiuj LinkedIn
                                        </Button>
                                      ) : (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => handleSearchCompetitorLinkedIn(competitor.domain)}
                                          disabled={competitorLinkedInUrls[competitor.domain]?.status === 'loading'}
                                        >
                                          {competitorLinkedInUrls[competitor.domain]?.status === 'loading' ? (
                                            <>
                                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                              Szukam...
                                            </>
                                          ) : (
                                            <>
                                              <LinkIcon className="h-3 w-3 mr-1" />
                                              Znajdź LinkedIn
                                            </>
                                          )}
                                        </Button>
                                      )}
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => {
                                          navigator.clipboard.writeText(competitor.domain);
                                          toast({
                                            title: "Skopiowano!",
                                            description: "Domena została skopiowana"
                                          });
                                        }}
                                      >
                                        <Copy className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          Nie znaleziono konkurentów
                        </div>
                      )}
                    </div>
                  )}

                  {!competitionResults && !searchingCompetition && (
                    <div className="text-center py-12 text-muted-foreground">
                      <Sparkles className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Wprowadź domenę firmy aby znaleźć konkurencję</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="automated-testing">
            <AutomatedTestingPanel />
          </TabsContent>

          <TabsContent value="headlines">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg md:text-xl">A/B Testing Nagłówków</CardTitle>
              </CardHeader>
              <CardContent>
                <HeadlineVariantsManager />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="pageleads">
            <PageleadsIntegrationDocs />
          </TabsContent>

          <TabsContent value="reports">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg md:text-xl">Podgląd Raportów</CardTitle>
              </CardHeader>
              <CardContent>
                <AdminReportPreview />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="files">
            <SimpleLandingCMS />
          </TabsContent>

          <TabsContent value="scoring">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg md:text-xl">Szczegóły Punktacji</CardTitle>
              </CardHeader>
              <CardContent>
                <AdminScoringDetails />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="brightdata-test">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg md:text-xl">🔬 Test Bright Data API</CardTitle>
                <p className="text-sm text-muted-foreground mt-2">
                  Testuj wzbogacanie danych LinkedIn o firmy przez Bright Data API
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="brightdata-url">LinkedIn Company URL</Label>
                    <Input
                      id="brightdata-url"
                      placeholder="https://www.linkedin.com/company/eltrox/"
                      value={testLinkedInUrl}
                      onChange={(e) => setTestLinkedInUrl(e.target.value)}
                      disabled={testingBrightData}
                    />
                    <p className="text-xs text-muted-foreground">
                      Wprowadź pełny URL profilu firmy na LinkedIn
                    </p>
                  </div>
                  
                  <Button
                    onClick={async () => {
                      setTestingBrightData(true);
                      setBrightDataResult(null);
                      
                      try {
                        const { data, error } = await supabase.functions.invoke('enrich-linkedin-brightdata', {
                          body: { linkedinUrl: testLinkedInUrl }
                        });
                        
                        if (error) throw error;
                        
                        setBrightDataResult(data);
                        
                        if (data.success) {
                          toast({
                            title: "✅ Dane pobrane!",
                            description: `Snapshot ID: ${data.snapshot_id}`
                          });
                        } else {
                          toast({
                            title: "❌ Błąd",
                            description: data.error || "Nie udało się pobrać danych",
                            variant: "destructive"
                          });
                        }
                      } catch (err: any) {
                        console.error(err);
                        toast({
                          title: "❌ Błąd",
                          description: err.message,
                          variant: "destructive"
                        });
                        setBrightDataResult({ error: err.message });
                      } finally {
                        setTestingBrightData(false);
                      }
                    }}
                    disabled={testingBrightData || !testLinkedInUrl}
                  >
                    {testingBrightData ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Testowanie... (może potrwać ~30-60s)
                      </>
                    ) : (
                      <>
                        <Sparkles className="mr-2 h-4 w-4" />
                        🚀 Test API
                      </>
                    )}
                  </Button>
                  
                  {brightDataResult && (
                    <div className="space-y-4 mt-6">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold">Wynik:</h4>
                        <Badge variant={brightDataResult.success ? "default" : "destructive"}>
                          {brightDataResult.success ? "✅ Sukces" : "❌ Błąd"}
                        </Badge>
                      </div>
                      
                      {brightDataResult.success && brightDataResult.data && (
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-muted-foreground">Snapshot ID:</span>
                              <span className="ml-2 font-mono text-xs">{brightDataResult.snapshot_id}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Timestamp:</span>
                              <span className="ml-2">{new Date(brightDataResult.timestamp).toLocaleString('pl-PL')}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Records:</span>
                              <span className="ml-2 font-medium">
                                {Array.isArray(brightDataResult.data) ? brightDataResult.data.length : 1}
                              </span>
                            </div>
                          </div>
                          
                          <div className="bg-muted p-4 rounded-lg">
                            <h5 className="font-semibold mb-2 text-sm">Surowe dane JSON:</h5>
                            <pre className="text-xs overflow-auto max-h-[500px] bg-background p-3 rounded border">
                              {JSON.stringify(brightDataResult.data, null, 2)}
                            </pre>
                          </div>
                        </div>
                      )}
                      
                      {!brightDataResult.success && (
                        <div className="bg-destructive/10 border border-destructive/20 p-4 rounded-lg">
                          <h5 className="font-semibold mb-2 text-sm text-destructive">Błąd:</h5>
                          <p className="text-sm">{brightDataResult.error}</p>
                          {brightDataResult.stack && (
                            <pre className="text-xs mt-2 text-muted-foreground overflow-auto max-h-[200px]">
                              {brightDataResult.stack}
                            </pre>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="bizraport-test">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg md:text-xl">🏢 Test Bizraport API</CardTitle>
                <p className="text-sm text-muted-foreground mt-2">
                  Testuj bezpośrednie odpytanie API Bizraport przez numer KRS
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="test-krs">Numer KRS (10 cyfr)</Label>
                    <div className="flex gap-2">
                      <Input
                        id="test-krs"
                        placeholder="np. 0000056844"
                        value={testKrs}
                        onChange={(e) => setTestKrs(e.target.value)}
                        maxLength={10}
                        className="font-mono"
                      />
                      <Button
                        onClick={async () => {
                          if (!testKrs || testKrs.length !== 10) {
                            toast({
                              title: "Błąd",
                              description: "Podaj prawidłowy 10-cyfrowy numer KRS",
                              variant: "destructive",
                            });
                            return;
                          }

                          setTestingBizraport(true);
                          setBizraportTestResult(null);

                          try {
                            const { data, error } = await supabase.functions.invoke('test-bizraport-api', {
                              body: { krs: testKrs }
                            });

                            if (error) throw error;
                            
                            setBizraportTestResult(data);
                            
                            if (data.success) {
                              toast({
                                title: "Sukces!",
                                description: `Pobrano dane dla: ${data.basic_info?.nazwa || 'firma'}`,
                              });
                            }
                          } catch (error: any) {
                            console.error('Bizraport API test error:', error);
                            setBizraportTestResult({
                              success: false,
                              error: error.message || 'Nieznany błąd'
                            });
                            toast({
                              title: "Błąd",
                              description: error.message || "Nie udało się pobrać danych",
                              variant: "destructive",
                            });
                          } finally {
                            setTestingBizraport(false);
                          }
                        }}
                        disabled={testingBizraport || !testKrs || testKrs.length !== 10}
                      >
                        {testingBizraport ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Testuję...
                          </>
                        ) : (
                          <>
                            <Play className="mr-2 h-4 w-4" />
                            Testuj API
                          </>
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Przykładowe KRS: 0000056844 (PKN Orlen), 0000028860 (PZU)
                    </p>
                  </div>

                  {bizraportTestResult && (
                    <div className="space-y-4">
                      <div className="flex items-center gap-2">
                        {bizraportTestResult.success ? (
                          <Badge variant="default" className="bg-green-600">
                            <CheckCircle2 className="mr-1 h-3 w-3" />
                            Sukces
                          </Badge>
                        ) : (
                          <Badge variant="destructive">
                            <XCircle className="mr-1 h-3 w-3" />
                            Błąd
                          </Badge>
                        )}
                      </div>
                      
                      {bizraportTestResult.success && (
                        <div className="space-y-4">
                          {/* Basic Info */}
                          <Card>
                            <CardHeader>
                              <CardTitle className="text-base">Informacje podstawowe</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-2 text-sm">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <span className="text-muted-foreground">Nazwa:</span>
                                  <p className="font-medium">{bizraportTestResult.basic_info?.nazwa || 'N/A'}</p>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Forma prawna:</span>
                                  <p className="font-medium">{bizraportTestResult.basic_info?.forma_prawna || 'N/A'}</p>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">KRS:</span>
                                  <p className="font-mono">{bizraportTestResult.krs}</p>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">NIP:</span>
                                  <p className="font-mono">{bizraportTestResult.nip || 'N/A'}</p>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">REGON:</span>
                                  <p className="font-mono">{bizraportTestResult.basic_info?.regon || 'N/A'}</p>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Kapitał zakładowy:</span>
                                  <p className="font-medium">{bizraportTestResult.basic_info?.kapital_zakladowy || 'N/A'}</p>
                                </div>
                              </div>
                              
                              <div className="pt-2 border-t">
                                <span className="text-muted-foreground">Adres:</span>
                                <p className="font-medium">
                                  {bizraportTestResult.basic_info?.ulica}, {bizraportTestResult.basic_info?.kod_pocztowy} {bizraportTestResult.basic_info?.miejscowosc}
                                </p>
                              </div>
                              
                              {bizraportTestResult.basic_info?.strona_www && (
                                <div>
                                  <span className="text-muted-foreground">WWW:</span>
                                  <p className="font-medium">{bizraportTestResult.basic_info.strona_www}</p>
                                </div>
                              )}
                            </CardContent>
                          </Card>

                          {/* Financial Data */}
                          {bizraportTestResult.financial_data && (
                            <Card>
                              <CardHeader>
                                <CardTitle className="text-base">Dane finansowe</CardTitle>
                              </CardHeader>
                              <CardContent className="space-y-2 text-sm">
                                {Object.entries(bizraportTestResult.financial_data).map(([key, data]: [string, any]) => {
                                  if (!data || !data.value) return null;
                                  return (
                                    <div key={key} className="flex justify-between items-center p-2 bg-muted rounded">
                                      <span className="capitalize">{key.replace(/_/g, ' ')}:</span>
                                      <span className="font-medium">
                                        {typeof data.value === 'number' ? formatNumberWithCommas(data.value) : data.value} PLN
                                        {data.year && <span className="text-xs text-muted-foreground ml-2">({data.year})</span>}
                                      </span>
                                    </div>
                                  );
                                })}
                              </CardContent>
                            </Card>
                          )}

                          {/* PKD */}
                          <Card>
                            <CardHeader>
                              <CardTitle className="text-base">Działalność</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-2 text-sm">
                              <div>
                                <span className="text-muted-foreground">PKD:</span>
                                <p className="font-mono">{bizraportTestResult.kod_pkd}</p>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Opis:</span>
                                <p>{bizraportTestResult.opis_pkd}</p>
                              </div>
                            </CardContent>
                          </Card>

                          {/* Raw JSON */}
                          <Card>
                            <CardHeader>
                              <CardTitle className="text-base">Surowe dane JSON</CardTitle>
                            </CardHeader>
                            <CardContent>
                              <pre className="text-xs bg-muted p-4 rounded overflow-auto max-h-[400px]">
                                {JSON.stringify(bizraportTestResult, null, 2)}
                              </pre>
                            </CardContent>
                          </Card>
                        </div>
                      )}
                      
                      {!bizraportTestResult.success && (
                        <div className="bg-destructive/10 border border-destructive/20 p-4 rounded-lg">
                          <h5 className="font-semibold mb-2 text-sm text-destructive">Błąd:</h5>
                          <p className="text-sm">{bizraportTestResult.error}</p>
                          {bizraportTestResult.details && (
                            <pre className="text-xs mt-2 text-muted-foreground overflow-auto max-h-[200px]">
                              {bizraportTestResult.details}
                            </pre>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Czy na pewno chcesz usunąć?</AlertDialogTitle>
            <AlertDialogDescription>
              Ta akcja jest nieodwracalna. Rekord zostanie trwale usunięty z bazy danych.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anuluj</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Usuń</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Usunąć {selectedSubmissions.size} zaznaczonych rekordów?</AlertDialogTitle>
            <AlertDialogDescription>
              Ta akcja jest nieodwracalna. Wszystkie zaznaczone rekordy (quizy, leady i SSA) zostaną trwale usunięte z bazy danych.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anuluj</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Usuń {selectedSubmissions.size} rekordów
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={showEnrichDialog} onOpenChange={() => {
        setShowEnrichDialog(false);
        setSelectedQuizSubmission(null);
        setSelectedSubmission(null);
      }}>
        <DialogContent className="max-w-[95vw] md:max-w-2xl max-h-[85vh] overflow-y-auto p-4 md:p-6">
          <DialogHeader>
            <DialogTitle>
              {selectedQuizSubmission ? 'Wzbogacone dane AI firmy' : 'Wzbogacone dane AI'}
            </DialogTitle>
            <DialogDescription>
              {selectedQuizSubmission ? 'Informacje o firmie zebrane przez AI' : 'Informacje o firmie zebrane przez AI'}
            </DialogDescription>
          </DialogHeader>
          {selectedQuizSubmission?.competitor_analysis && (
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg border">
                <strong>Domena:</strong> {selectedQuizSubmission.domain_input}
              </div>
              <div className="p-4 bg-muted rounded-lg border">
                <strong>Confidence:</strong> {selectedQuizSubmission.competitor_analysis.confidence || 'unknown'}
              </div>
              {selectedQuizSubmission.competitor_analysis.competitors && selectedQuizSubmission.competitor_analysis.competitors.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2">Znalezieni konkurenci ({selectedQuizSubmission.competitor_analysis.competitors.length}):</h3>
                  <div className="space-y-2">
                    {selectedQuizSubmission.competitor_analysis.competitors.map((comp: any, idx: number) => {
                      // Handle both string format (e.g., "domain.com") and object format (e.g., { domain: "...", company_name: "..." })
                      const domain = typeof comp === 'string' ? comp : comp.domain;
                      const companyName = typeof comp === 'string' ? comp : (comp.company_name || comp.domain);
                      
                      return (
                        <div key={idx} className="p-3 bg-background rounded-lg border">
                          <div><strong>Firma:</strong> {companyName}</div>
                          {typeof comp !== 'string' && comp.domain && (
                            <div className="text-sm text-muted-foreground">{comp.domain}</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
          {selectedQuizSubmission?.enriched_data && (
            <div className="space-y-4">
              {/* Data Quality Score */}
              {selectedQuizSubmission.enriched_data.data_quality_score !== undefined && (
                <div className="p-4 bg-muted rounded-lg border">
                  <strong>Data Quality Score:</strong>
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex-1 bg-background rounded-full h-3 overflow-hidden">
                      <div 
                        className={`h-full transition-all ${
                          selectedQuizSubmission.enriched_data.data_quality_score >= 70 ? 'bg-green-500' :
                          selectedQuizSubmission.enriched_data.data_quality_score >= 40 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${selectedQuizSubmission.enriched_data.data_quality_score}%` }}
                      />
                    </div>
                    <span className="font-bold">{selectedQuizSubmission.enriched_data.data_quality_score}/100</span>
                  </div>
                </div>
              )}
              
              <div className="p-4 bg-muted rounded-lg border">
                <pre className="text-xs overflow-auto whitespace-pre-wrap">
                  {JSON.stringify(selectedQuizSubmission.enriched_data, null, 2)}
                </pre>
              </div>
            </div>
          )}
          {selectedSubmission?.enriched_data && (
            <div className="space-y-4">
              {/* Data Quality Score */}
              {selectedSubmission.enriched_data.data_quality_score !== undefined && (
                <div className="p-4 bg-muted rounded-lg border">
                  <strong>Data Quality Score:</strong>
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex-1 bg-background rounded-full h-3 overflow-hidden">
                      <div 
                        className={`h-full transition-all ${
                          selectedSubmission.enriched_data.data_quality_score >= 70 ? 'bg-green-500' :
                          selectedSubmission.enriched_data.data_quality_score >= 40 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${selectedSubmission.enriched_data.data_quality_score}%` }}
                      />
                    </div>
                    <span className="font-bold">{selectedSubmission.enriched_data.data_quality_score}/100</span>
                  </div>
                </div>
              )}

              {/* Validation Status */}
              {selectedSubmission.enriched_data.bizraport_validation && (
                <div className="p-4 bg-muted rounded-lg border space-y-2">
                  <div>
                    <strong>Bizraport.pl Validation:</strong>
                    <span className={`ml-2 px-2 py-1 rounded text-xs font-semibold ${
                      selectedSubmission.enriched_data.bizraport_validation.status === 'verified' ? 'bg-green-100 text-green-800' :
                      selectedSubmission.enriched_data.bizraport_validation.status === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {selectedSubmission.enriched_data.bizraport_validation.status.toUpperCase()}
                    </span>
                  </div>
                  {selectedSubmission.enriched_data.bizraport_validation.nip_format_valid !== undefined && (
                    <div className="text-sm">
                      <strong>NIP Format:</strong> {selectedSubmission.enriched_data.bizraport_validation.nip_format_valid ? '✓ Valid' : '✗ Invalid'}
                    </div>
                  )}
                  {selectedSubmission.enriched_data.bizraport_validation.krs_format_valid !== undefined && (
                    <div className="text-sm">
                      <strong>KRS Format:</strong> {selectedSubmission.enriched_data.bizraport_validation.krs_format_valid ? '✓ Valid' : '✗ Invalid'}
                    </div>
                  )}
                  {selectedSubmission.enriched_data.bizraport_validation.company_name_match !== undefined && (
                    <div className="text-sm">
                      <strong>Company Name Match:</strong> {selectedSubmission.enriched_data.bizraport_validation.company_name_match ? '✓ Match' : '✗ No Match'}
                    </div>
                  )}
                  {selectedSubmission.enriched_data.bizraport_validation.data_sources_used && (
                    <div className="text-sm">
                      <strong>Sources Used:</strong> {selectedSubmission.enriched_data.bizraport_validation.data_sources_used.join(', ')}
                    </div>
                  )}
                  {selectedSubmission.enriched_data.bizraport_validation.validation_notes && (
                    <div className="text-sm text-muted-foreground mt-2">
                      <strong>Notes:</strong> {selectedSubmission.enriched_data.bizraport_validation.validation_notes}
                    </div>
                  )}
                </div>
              )}

              {selectedSubmission.enriched_data.company_name && (
                <div>
                  <h3 className="font-semibold">Nazwa firmy</h3>
                  <p>{selectedSubmission.enriched_data.company_name}</p>
                </div>
              )}
              {selectedSubmission.enriched_data.company_description && (
                <div>
                  <h3 className="font-semibold">Opis firmy</h3>
                  <p className="text-sm text-muted-foreground">{selectedSubmission.enriched_data.company_description}</p>
                </div>
              )}
              
              {selectedSubmission.enriched_data.company_news && Array.isArray(selectedSubmission.enriched_data.company_news) && selectedSubmission.enriched_data.company_news.length > 0 && (
                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <span>📰</span>
                    <span>Newsy o firmie</span>
                  </h3>
                  <ul className="space-y-2">
                    {selectedSubmission.enriched_data.company_news.map((news: any, idx: number) => (
                      <li key={idx} className="text-sm">
                        <a href={news.link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">
                          {news.title}
                        </a>
                        {news.source && <span className="text-muted-foreground ml-2">({news.source})</span>}
                        {news.date && <span className="text-muted-foreground ml-2 text-xs">{news.date}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedSubmission.enriched_data.person_position && (
                <div>
                  <h3 className="font-semibold">Stanowisko osoby</h3>
                  <p>{selectedSubmission.enriched_data.person_position}</p>
                </div>
              )}
              {selectedSubmission.enriched_data.person_linkedin && (
                <div>
                  <h3 className="font-semibold">LinkedIn osoby</h3>
                  <a href={selectedSubmission.enriched_data.person_linkedin} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    {selectedSubmission.enriched_data.person_linkedin}
                  </a>
                </div>
              )}
              {selectedSubmission.enriched_data.employees_estimate && (
                <div>
                  <h3 className="font-semibold">Szacowana liczba pracowników</h3>
                  <p>{selectedSubmission.enriched_data.employees_estimate}</p>
                </div>
              )}
              {selectedSubmission.enriched_data.revenue_estimate && (
                <div>
                  <h3 className="font-semibold">Szacowane przychody</h3>
                  <p>{selectedSubmission.enriched_data.revenue_estimate}</p>
                </div>
              )}
              {selectedSubmission.enriched_data.industry && (
                <div>
                  <h3 className="font-semibold">Branża</h3>
                  <p>{selectedSubmission.enriched_data.industry}</p>
                </div>
              )}
              {selectedSubmission.enriched_data.headquarters && (
                <div>
                  <h3 className="font-semibold">Siedziba</h3>
                  <p>{selectedSubmission.enriched_data.headquarters}</p>
                </div>
              )}
              {(selectedSubmission.enriched_data.foundation_year || selectedSubmission.enriched_data.founded_year) && (
                <div>
                  <h3 className="font-semibold">Rok założenia</h3>
                  <p>{selectedSubmission.enriched_data.foundation_year || selectedSubmission.enriched_data.founded_year}</p>
                </div>
              )}
              {selectedSubmission.enriched_data.linkedin_url && (
                <div>
                  <h3 className="font-semibold">LinkedIn firmy</h3>
                  <a href={selectedSubmission.enriched_data.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    {selectedSubmission.enriched_data.linkedin_url}
                  </a>
                </div>
              )}
              {/* Quick Links Section */}
              <div className="flex flex-wrap gap-3 mb-4">
                {/* Bizraport Link */}
                {(selectedSubmission.enriched_data.bizraport_data?.nip || selectedSubmission.enriched_data.bizraport_url) && (
                  <a 
                    href={selectedSubmission.enriched_data.bizraport_url || `https://www.bizraport.pl/firma/${selectedSubmission.enriched_data.bizraport_data.nip}`}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Bizraport.pl
                  </a>
                )}
                
                {/* LinkedIn Company Link */}
                {selectedSubmission.enriched_data.linkedin_url && (
                  <a 
                    href={selectedSubmission.enriched_data.linkedin_url}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#0A66C2] hover:bg-[#004182] text-white rounded-lg font-semibold transition-colors"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                    </svg>
                    LinkedIn Firmy
                  </a>
                )}
                
                {/* LinkedIn Person Link */}
                {selectedSubmission.enriched_data.person_linkedin && (
                  <a 
                    href={selectedSubmission.enriched_data.person_linkedin}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#0A66C2] hover:bg-[#004182] text-white rounded-lg font-semibold transition-colors"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                    </svg>
                    LinkedIn Osoby
                  </a>
                )}
              </div>
              
              {/* Bizraport.pl Data */}
              {selectedSubmission.enriched_data.bizraport_data && (
                <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border-2 border-blue-200 space-y-3">
                  <div className="font-bold text-lg text-blue-900 mb-3">📊 Dane rejestrowe (Bizraport)</div>

                  <div className="grid grid-cols-2 gap-3">
                    {selectedSubmission.enriched_data.bizraport_data.nip && (
                      <div className="bg-white/60 p-3 rounded">
                        <div className="text-xs text-blue-700 font-semibold">NIP</div>
                        <div className="text-lg font-bold text-blue-900">{selectedSubmission.enriched_data.bizraport_data.nip}</div>
                      </div>
                    )}
                    {selectedSubmission.enriched_data.bizraport_data.krs && (
                      <div className="bg-white/60 p-3 rounded">
                        <div className="text-xs text-blue-700 font-semibold">KRS</div>
                        <div className="text-lg font-bold text-blue-900">{selectedSubmission.enriched_data.bizraport_data.krs}</div>
                      </div>
                    )}
                    {selectedSubmission.enriched_data.bizraport_data.regon && (
                      <div className="bg-white/60 p-3 rounded">
                        <div className="text-xs text-blue-700 font-semibold">REGON</div>
                        <div className="text-lg font-bold text-blue-900">{selectedSubmission.enriched_data.bizraport_data.regon}</div>
                      </div>
                    )}
                    {selectedSubmission.enriched_data.bizraport_data.share_capital && (
                      <div className="bg-white/60 p-3 rounded">
                        <div className="text-xs text-blue-700 font-semibold">Kapitał zakładowy</div>
                        <div className="text-lg font-bold text-blue-900">{selectedSubmission.enriched_data.bizraport_data.share_capital}</div>
                      </div>
                    )}
                  </div>

                  {/* Beautiful Financial Summary Cards */}
                  {selectedSubmission.enriched_data.bizraport_data.financial_summary && (() => {
                    const financialData = selectedSubmission.enriched_data.bizraport_data.financial_summary.split('; ').reduce((acc: any, item: string) => {
                      const [label, value] = item.split(': ');
                      acc[label] = value;
                      return acc;
                    }, {});
                    
                    const cardConfigs = [
                      { 
                        label: 'Aktywa', 
                        value: financialData['Aktywa'], 
                        icon: '📊',
                        bgGradient: 'from-blue-50 to-blue-100',
                        borderColor: 'border-blue-300',
                        textColor: 'text-blue-700',
                        valueColor: 'text-blue-900'
                      },
                      { 
                        label: 'Przychód', 
                        value: financialData['Przychody'] || financialData['Przychód'], 
                        icon: '💰',
                        bgGradient: 'from-green-50 to-green-100',
                        borderColor: 'border-green-300',
                        textColor: 'text-green-700',
                        valueColor: 'text-green-900'
                      },
                      { 
                        label: 'Zysk', 
                        value: financialData['Zysk'], 
                        icon: '📈',
                        bgGradient: 'from-orange-50 to-orange-100',
                        borderColor: 'border-orange-300',
                        textColor: 'text-orange-700',
                        valueColor: 'text-orange-900'
                      },
                      { 
                        label: 'Wartość firmy', 
                        value: financialData['Wartość firmy'], 
                        icon: '💎',
                        bgGradient: 'from-purple-50 to-purple-100',
                        borderColor: 'border-purple-300',
                        textColor: 'text-purple-700',
                        valueColor: 'text-purple-900'
                      }
                    ].filter(card => card.value);

                    return cardConfigs.length > 0 ? (
                      <div className="mt-4">
                        <div className="font-bold text-gray-800 mb-3 text-sm">Dane finansowe</div>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                          {cardConfigs.map((card, idx) => (
                            <div 
                              key={idx}
                              className={`bg-gradient-to-br ${card.bgGradient} p-4 rounded-xl border-2 ${card.borderColor} shadow-sm hover:shadow-md transition-shadow`}
                            >
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-2xl">{card.icon}</span>
                                <div className={`text-xs font-semibold ${card.textColor}`}>{card.label}</div>
                              </div>
                              <div className={`text-2xl font-bold ${card.valueColor}`}>
                                {formatNumberWithCommas(card.value)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null;
                  })()}

                  {selectedSubmission.enriched_data.bizraport_data.legal_form && (
                    <div className="text-sm bg-white/60 p-2 rounded">
                      <strong className="text-blue-800">Forma prawna:</strong> {selectedSubmission.enriched_data.bizraport_data.legal_form}
                    </div>
                  )}
                  {selectedSubmission.enriched_data.bizraport_data.year_established && (
                    <div className="text-sm bg-white/60 p-2 rounded">
                      <strong className="text-blue-800">Rok założenia:</strong> {selectedSubmission.enriched_data.bizraport_data.year_established}
                    </div>
                  )}
                  {selectedSubmission.enriched_data.bizraport_data.pkd_codes && selectedSubmission.enriched_data.bizraport_data.pkd_codes.length > 0 && (
                    <div className="text-sm bg-white/60 p-2 rounded">
                      <strong className="text-blue-800">PKD Codes:</strong> {selectedSubmission.enriched_data.bizraport_data.pkd_codes.slice(0, 5).join(', ')}
                      {selectedSubmission.enriched_data.bizraport_data.pkd_codes.length > 5 && ` (+${selectedSubmission.enriched_data.bizraport_data.pkd_codes.length - 5} more)`}
                    </div>
                  )}
                </div>
              )}

              {/* PhantomBuster Status */}
              {selectedSubmission.enriched_data.debug_info?.phantombuster_enabled && (
                <div className="p-4 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-lg border-2 border-purple-200">
                  <div className="font-bold text-lg text-purple-900 mb-3">🤖 PhantomBuster Status</div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Badge variant={selectedSubmission.enriched_data.debug_info.phantombuster_person_used ? "default" : "outline"}>
                        Person: {selectedSubmission.enriched_data.debug_info.phantombuster_person_used ? '✓ Used' : '✗ Not used'}
                      </Badge>
                      <span className="text-sm text-purple-700">
                        Method: {selectedSubmission.enriched_data.debug_info.person_linkedin_method || 'N/A'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={selectedSubmission.enriched_data.debug_info.phantombuster_company_used ? "default" : "outline"}>
                        Company: {selectedSubmission.enriched_data.debug_info.phantombuster_company_used ? '✓ Used' : '✗ Not used'}
                      </Badge>
                      <span className="text-sm text-purple-700">
                        Method: {selectedSubmission.enriched_data.debug_info.company_linkedin_method || 'N/A'}
                      </span>
                    </div>
                    {(selectedSubmission.enriched_data.debug_info.person_data_quality_score || 
                      selectedSubmission.enriched_data.debug_info.company_data_quality_score) && (
                      <div className="mt-2 pt-2 border-t border-purple-200">
                        <div className="text-sm text-purple-900 font-semibold">Data Quality Scores:</div>
                        {selectedSubmission.enriched_data.debug_info.person_data_quality_score && (
                          <div className="text-sm text-purple-700">
                            Person: {selectedSubmission.enriched_data.debug_info.person_data_quality_score}/100
                          </div>
                        )}
                        {selectedSubmission.enriched_data.debug_info.company_data_quality_score && (
                          <div className="text-sm text-purple-700">
                            Company: {selectedSubmission.enriched_data.debug_info.company_data_quality_score}/100
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* LinkedIn Profile Data (from PhantomBuster) */}
              {selectedSubmission.enriched_data.linkedin_profile_data && (
                <div className="p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg border-2 border-blue-200">
                  <div className="font-bold text-lg text-blue-900 mb-3">👤 LinkedIn Profile Data</div>
                  <div className="space-y-2 text-sm">
                    {selectedSubmission.enriched_data.linkedin_profile_data.fullName && (
                      <div><strong className="text-blue-800">Name:</strong> {selectedSubmission.enriched_data.linkedin_profile_data.fullName}</div>
                    )}
                    {selectedSubmission.enriched_data.linkedin_profile_data.headline && (
                      <div><strong className="text-blue-800">Headline:</strong> {selectedSubmission.enriched_data.linkedin_profile_data.headline}</div>
                    )}
                    {selectedSubmission.enriched_data.linkedin_profile_data.location && (
                      <div><strong className="text-blue-800">Location:</strong> {selectedSubmission.enriched_data.linkedin_profile_data.location}</div>
                    )}
                    {selectedSubmission.enriched_data.linkedin_profile_data.connections && (
                      <div><strong className="text-blue-800">Connections:</strong> {selectedSubmission.enriched_data.linkedin_profile_data.connections}</div>
                    )}
                    {selectedSubmission.enriched_data.linkedin_profile_data.emails && selectedSubmission.enriched_data.linkedin_profile_data.emails.length > 0 && (
                      <div><strong className="text-blue-800">Emails:</strong> {selectedSubmission.enriched_data.linkedin_profile_data.emails.join(', ')}</div>
                    )}
                    {selectedSubmission.enriched_data.linkedin_profile_data.skills && selectedSubmission.enriched_data.linkedin_profile_data.skills.length > 0 && (
                      <div><strong className="text-blue-800">Skills:</strong> {selectedSubmission.enriched_data.linkedin_profile_data.skills.slice(0, 10).join(', ')}</div>
                    )}
                  </div>
                </div>
              )}

              {/* LinkedIn Company Data (from PhantomBuster) */}
              {selectedSubmission.enriched_data.linkedin_company_data && (
                <div className="p-4 bg-gradient-to-br from-green-50 to-teal-50 rounded-lg border-2 border-green-200">
                  <div className="font-bold text-lg text-green-900 mb-3">🏢 LinkedIn Company Data</div>
                  <div className="space-y-2 text-sm">
                    {selectedSubmission.enriched_data.linkedin_company_data.name && (
                      <div><strong className="text-green-800">Name:</strong> {selectedSubmission.enriched_data.linkedin_company_data.name}</div>
                    )}
                    {selectedSubmission.enriched_data.linkedin_company_data.description && (
                      <div><strong className="text-green-800">Description:</strong> {selectedSubmission.enriched_data.linkedin_company_data.description}</div>
                    )}
                    {selectedSubmission.enriched_data.linkedin_company_data.industry && (
                      <div><strong className="text-green-800">Industry:</strong> {selectedSubmission.enriched_data.linkedin_company_data.industry}</div>
                    )}
                    {selectedSubmission.enriched_data.linkedin_company_data.size && (
                      <div><strong className="text-green-800">Size:</strong> {selectedSubmission.enriched_data.linkedin_company_data.size}</div>
                    )}
                    {selectedSubmission.enriched_data.linkedin_company_data.headquarters && (
                      <div><strong className="text-green-800">Headquarters:</strong> {selectedSubmission.enriched_data.linkedin_company_data.headquarters}</div>
                    )}
                    {selectedSubmission.enriched_data.linkedin_company_data.founded && (
                      <div><strong className="text-green-800">Founded:</strong> {selectedSubmission.enriched_data.linkedin_company_data.founded}</div>
                    )}
                    {selectedSubmission.enriched_data.linkedin_company_data.followerCount && (
                      <div><strong className="text-green-800">Followers:</strong> {selectedSubmission.enriched_data.linkedin_company_data.followerCount}</div>
                    )}
                    {selectedSubmission.enriched_data.linkedin_company_data.specialties && selectedSubmission.enriched_data.linkedin_company_data.specialties.length > 0 && (
                      <div><strong className="text-green-800">Specialties:</strong> {selectedSubmission.enriched_data.linkedin_company_data.specialties.join(', ')}</div>
                    )}
                  </div>
                </div>
              )}

              {/* Alternative Sources */}
              {selectedSubmission.enriched_data.alternative_sources && Object.keys(selectedSubmission.enriched_data.alternative_sources).length > 0 && (
                <div className="p-4 bg-muted rounded-lg border space-y-2">
                  <div className="font-bold">Alternative Sources:</div>
                  {selectedSubmission.enriched_data.alternative_sources.linkedin_company && (
                    <div className="text-sm">
                      <strong>LinkedIn:</strong>{' '}
                      <a href={selectedSubmission.enriched_data.alternative_sources.linkedin_company} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        {selectedSubmission.enriched_data.alternative_sources.linkedin_company}
                      </a>
                    </div>
                  )}
                  {selectedSubmission.enriched_data.alternative_sources.rejestr_io && (
                    <div className="text-sm">
                      <strong>Rejestr.io:</strong>{' '}
                      <a href={selectedSubmission.enriched_data.alternative_sources.rejestr_io} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        {selectedSubmission.enriched_data.alternative_sources.rejestr_io}
                      </a>
                    </div>
                  )}
                  {selectedSubmission.enriched_data.alternative_sources.golden_com && (
                    <div className="text-sm">
                      <strong>Golden.com:</strong>{' '}
                      <a href={selectedSubmission.enriched_data.alternative_sources.golden_com} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        {selectedSubmission.enriched_data.alternative_sources.golden_com}
                      </a>
                    </div>
                  )}
                </div>
              )}

              {selectedSubmission.enriched_data.research_notes && (
                <div className="text-sm text-muted-foreground">
                  <strong>Research Notes:</strong> {selectedSubmission.enriched_data.research_notes}
                </div>
              )}
              
              {/* Debug LinkedIn Enrichment */}
              {selectedSubmission.enriched_data.debug_info && (
                <details className="p-4 bg-muted rounded-lg border">
                  <summary className="font-bold cursor-pointer hover:text-primary">
                    🔍 Debug: LinkedIn Enrichment Details
                  </summary>
                  <div className="mt-4 space-y-4">
                    {/* LinkedIn Person */}
                    <div className="p-3 bg-background rounded">
                      <div className="font-semibold mb-2">LinkedIn Person Search:</div>
                      <div className="text-sm space-y-1">
                        <div>✓ Found: {selectedSubmission.enriched_data.debug_info.linkedin_person_found ? 'Yes' : 'No'}</div>
                        <div>✓ Score: {selectedSubmission.enriched_data.debug_info.linkedin_person_score || 0}</div>
                        <div>✓ AI Used: {selectedSubmission.enriched_data.debug_info.linkedin_person_ai_used ? 'Yes (AI Inference)' : 'No (SERP)'}</div>
                        {selectedSubmission.enriched_data.debug_info.linkedin_person_ai_used && (
                          <div className="text-yellow-600">⚠️ Result inferred by AI (confidence: medium)</div>
                        )}
                      </div>
                    </div>
                    
                    {/* LinkedIn Company */}
                    <div className="p-3 bg-background rounded">
                      <div className="font-semibold mb-2">LinkedIn Company Search:</div>
                      <div className="text-sm space-y-1">
                        <div>✓ Found: {selectedSubmission.enriched_data.debug_info.linkedin_company_found ? 'Yes' : 'No'}</div>
                        <div>✓ Score: {selectedSubmission.enriched_data.debug_info.linkedin_company_score || 0}</div>
                        <div>✓ Candidates: {selectedSubmission.enriched_data.debug_info.linkedin_company_candidates || 0}</div>
                        <div>✓ AI Used: {selectedSubmission.enriched_data.debug_info.linkedin_company_ai_used ? 'Yes (AI Inference)' : 'No (SERP)'}</div>
                        {selectedSubmission.enriched_data.debug_info.linkedin_company_ai_used && (
                          <div className="text-yellow-600">⚠️ Result inferred by AI (confidence: medium)</div>
                        )}
                      </div>
                      
                      {/* Top Candidates */}
                      {selectedSubmission.enriched_data.debug_info.top_company_candidates && selectedSubmission.enriched_data.debug_info.top_company_candidates.length > 0 && (
                        <div className="mt-3">
                          <div className="font-semibold mb-2 text-xs text-muted-foreground">Top-3 Candidates:</div>
                          <div className="space-y-2">
                            {selectedSubmission.enriched_data.debug_info.top_company_candidates.map((candidate: any, idx: number) => (
                              <div key={idx} className="text-xs p-2 bg-muted rounded">
                                <div className="font-mono">{idx + 1}. {candidate.url}</div>
                                <div className="text-muted-foreground mt-1">
                                  Score: {candidate.score} | {candidate.title?.substring(0, 60)}...
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </details>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Quiz Answers Dialog */}
      <Dialog open={showQuizDialog} onOpenChange={() => {
        setShowQuizDialog(false);
        setSelectedQuizSubmission(null);
      }}>
        <DialogContent className="max-w-[95vw] md:max-w-3xl max-h-[85vh] overflow-y-auto p-4 md:p-6">
          <DialogHeader>
            <DialogTitle>Odpowiedzi z quizu</DialogTitle>
            <DialogDescription>
              Odpowiedzi użytkownika na pytania quizu LinkedIn Readiness
            </DialogDescription>
          </DialogHeader>
          {(selectedQuizSubmission?.quiz_answers || selectedSubmission?.quiz_answers) && (() => {
            const rawAnswers = (selectedQuizSubmission?.quiz_answers || selectedSubmission?.quiz_answers) || {};
            
            // Process answers - keep both numeric keys and string keys for interstitials
            const answers: Record<number | string, string | string[]> = {};
            Object.entries(rawAnswers).forEach(([key, value]) => {
              if (key.startsWith('interstitial_')) {
                // Keep interstitial keys as strings
                answers[key] = value as string | string[];
              } else {
                // Convert regular question keys to numbers
                const numKey = parseInt(key);
                if (!isNaN(numKey)) {
                  answers[numKey] = value as string | string[];
                }
              }
            });
            const quizBand = selectedSubmission?.quiz_band || selectedQuizSubmission?.quiz_band;
            const quizScore = selectedSubmission?.quiz_score || selectedQuizSubmission?.quiz_score;
            
            return (
              <div className="space-y-6">
                {/* Score Summary */}
                <div className="p-4 bg-primary/10 rounded-lg border border-primary/20">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold text-lg">Wynik końcowy</h3>
                    <Badge variant={getBandColor(quizBand)} className="text-lg px-4 py-1">
                      {getBandLabel(quizBand)}
                    </Badge>
                  </div>
                  <div className="text-3xl font-bold text-primary">
                    {quizScore}/10
                  </div>
                </div>

                {/* Questions and Answers */}
                <div className="space-y-4">
                  {/* Interstitials Answers */}
                  {Object.entries(interstitials).map(([id, data]) => {
                    const questionId = parseInt(id);
                    const answer = answers[`interstitial_${questionId}` as any];
                    
                    if (!answer) return null;
                    
                    const answerLabel = answer === 'yes' 
                      ? '✅ Tak, wiedziałem' 
                      : '❌ Nie wiedziałem';
                    
                    return (
                      <div key={`interstitial-${questionId}`} className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg border-2 border-blue-200 dark:border-blue-800">
                        <div className="mb-2">
                          <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">
                            📊 Przerywnik po pytaniu {questionId}
                          </span>
                          <h4 className="font-semibold text-base mt-1">
                            {data.title}
                          </h4>
                          <p className="text-sm text-muted-foreground mt-1">
                            {data.description.substring(0, 120)}...
                          </p>
                        </div>
                        <div className="mt-2">
                          <Badge variant="outline" className="bg-background">
                            {answerLabel}
                          </Badge>
                        </div>
                      </div>
                    );
                  })}

                  {/* Regular Questions */}
                  {questions.map((question: any) => {
                    const answer = answers[question.id];
                    if (!answer) return null;

                    const getAnswerLabels = () => {
                      // Special handling for dynamic questions (Q20 - competitors)
                      if (question.isDynamic) {
                        if (Array.isArray(answer)) {
                          // For dynamic questions, answer values are already the labels (domain names)
                          return answer.map((val) => ({ 
                            label: val, 
                            score: 0  // Dynamic questions don't contribute to score
                          }));
                        }
                        return [];
                      }
                      
                      // Regular handling for static questions
                      if (question.type === 'single' && typeof answer === 'string') {
                        const option = question.options.find((opt: any) => opt.value === answer);
                        return option ? [{ label: option.label, score: option.score }] : [];
                      } else if (question.type === 'multiple' && Array.isArray(answer)) {
                        return answer.map((val) => {
                          const option = question.options.find((opt: any) => opt.value === val);
                          return option ? { label: option.label, score: option.score } : null;
                        }).filter(Boolean);
                      }
                      return [];
                    };

                    const answerLabels = getAnswerLabels();

                    return (
                      <div key={question.id} className="p-4 bg-muted rounded-lg border">
                        <div className="mb-2">
                          <span className="text-xs font-semibold text-muted-foreground">
                            Pytanie {question.id}
                          </span>
                          <h4 className="font-semibold text-base mt-1">
                            {question.question}
                          </h4>
                          <span className="text-xs text-muted-foreground mt-1 block">
                            Typ: {question.type === 'single' ? 'Jednokrotny wybór' : 'Wielokrotny wybór'}
                          </span>
                        </div>
                        <div className="space-y-2 mt-3">
                          {answerLabels.map((ans: any, idx: number) => (
                            <div key={idx} className="flex items-center justify-between bg-background p-2 rounded">
                              <span className="text-sm">✓ {ans.label}</span>
                              {!question.isDynamic && (
                                <Badge variant="secondary" className="text-xs">
                                  +{ans.score} pkt
                                </Badge>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                      );
                  })}
                </div>

                {/* LinkedIn Profile Section */}
                {(() => {
                  const quizAnswers = selectedQuizSubmission?.quiz_answers || selectedSubmission?.quiz_answers;
                  const foundProfile = quizAnswers?.found_linkedin_profile;
                  const userConfirmation = rawAnswers[17] || rawAnswers[21];
                  
                  if (!foundProfile) return null;
                  
                  return (
                    <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-950 rounded-lg border-2 border-blue-200 dark:border-blue-800">
                      <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                        🔗 Znaleziony profil LinkedIn firmy
                      </h4>
                      
                      {/* Logo and basic info */}
                      <div className="space-y-3 text-sm">
                        {/* Logo and Title */}
                        <div className="flex items-center gap-3 pb-3 border-b border-blue-200 dark:border-blue-800">
                          {foundProfile.logo && (
                            <img 
                              src={foundProfile.logo} 
                              alt="Company logo" 
                              className="w-12 h-12 object-contain rounded"
                            />
                          )}
                          <div>
                            <div className="font-semibold">{foundProfile.title}</div>
                            <a 
                              href={foundProfile.linkedinUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline text-xs"
                            >
                              {foundProfile.linkedinUrl}
                            </a>
                          </div>
                        </div>

                        {/* Real Data - show Bright Data enriched info if available, otherwise old scrape data */}
                        <div className="bg-white dark:bg-gray-900 p-3 rounded border border-blue-100 dark:border-blue-900">
                          <div className="font-semibold mb-2 text-xs text-blue-700 dark:text-blue-300">
                            📊 {foundProfile.enriched ? 'Dane z Bright Data:' : 'Rzeczywiste dane ze scrape\'u:'}
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <span className="text-gray-600 dark:text-gray-400">Obserwujący:</span>{' '}
                              <span className="font-medium">{foundProfile.followerCount !== null && foundProfile.followerCount !== undefined ? foundProfile.followerCount.toLocaleString() : '❌ BRAK DANYCH'}</span>
                            </div>
                            <div>
                              <span className="text-gray-600 dark:text-gray-400">Pracownicy:</span>{' '}
                              <span className="font-medium">{foundProfile.employeeCount !== null && foundProfile.employeeCount !== undefined ? foundProfile.employeeCount.toLocaleString() : '❌ BRAK DANYCH'}</span>
                            </div>
                            <div>
                              <span className="text-gray-600 dark:text-gray-400">Branża:</span>{' '}
                              <span className="font-medium">{foundProfile.industry || '❌ BRAK DANYCH'}</span>
                            </div>
                            
                            {/* Bright Data specific fields */}
                            {foundProfile.enriched && (
                              <>
                                {foundProfile.founded && (
                                  <div>
                                    <span className="text-gray-600 dark:text-gray-400">Rok założenia:</span>{' '}
                                    <span className="font-medium">{foundProfile.founded}</span>
                                  </div>
                                )}
                                {foundProfile.headquarters && (
                                  <div>
                                    <span className="text-gray-600 dark:text-gray-400">Siedziba:</span>{' '}
                                    <span className="font-medium">{foundProfile.headquarters}</span>
                                  </div>
                                )}
                                {foundProfile.company_size && (
                                  <div>
                                    <span className="text-gray-600 dark:text-gray-400">Wielkość firmy:</span>{' '}
                                    <span className="font-medium">{foundProfile.company_size}</span>
                                  </div>
                                )}
                                {foundProfile.organization_type && (
                                  <div>
                                    <span className="text-gray-600 dark:text-gray-400">Typ organizacji:</span>{' '}
                                    <span className="font-medium">{foundProfile.organization_type}</span>
                                  </div>
                                )}
                                {foundProfile.website && (
                                  <div className="col-span-2">
                                    <span className="text-gray-600 dark:text-gray-400">Strona www:</span>{' '}
                                    <a href={foundProfile.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                      {foundProfile.website}
                                    </a>
                                  </div>
                                )}
                                {foundProfile.specialties && (
                                  <div className="col-span-2">
                                    <span className="text-gray-600 dark:text-gray-400">Specjalizacje:</span>{' '}
                                    <span className="font-medium">{foundProfile.specialties}</span>
                                  </div>
                                )}
                                {foundProfile.slogan && (
                                  <div className="col-span-2">
                                    <span className="text-gray-600 dark:text-gray-400">Slogan:</span>{' '}
                                    <span className="font-medium italic">{foundProfile.slogan}</span>
                                  </div>
                                )}
                                {foundProfile.description && (
                                  <div className="col-span-2 pt-2 border-t border-blue-100 dark:border-blue-900">
                                    <span className="text-gray-600 dark:text-gray-400 block mb-1">O firmie:</span>
                                    <p className="text-xs leading-relaxed">{foundProfile.description}</p>
                                  </div>
                                )}
                                {foundProfile.locations && foundProfile.locations.length > 0 && (
                                  <div className="col-span-2 pt-2 border-t border-blue-100 dark:border-blue-900">
                                    <span className="text-gray-600 dark:text-gray-400 block mb-1">Lokalizacje:</span>
                                    {foundProfile.locations.map((loc: string, idx: number) => (
                                      <div key={idx} className="text-xs">{loc}</div>
                                    ))}
                                  </div>
                                )}
                              </>
                            )}
                            
                            {/* Old scrape fields - only show if not enriched */}
                            {!foundProfile.enriched && (
                              <>
                                <div>
                                  <span className="text-gray-600 dark:text-gray-400">Częstotliwość postów:</span>{' '}
                                  <span className="font-medium">{foundProfile.postingFrequency || '❌ BRAK DANYCH'}</span>
                                </div>
                                <div>
                                  <span className="text-gray-600 dark:text-gray-400">Średni engagement:</span>{' '}
                                  <span className="font-medium">{foundProfile.avgEngagement !== null && foundProfile.avgEngagement !== undefined ? foundProfile.avgEngagement.toFixed(2) : '❌ BRAK DANYCH'}</span>
                                </div>
                                <div>
                                  <span className="text-gray-600 dark:text-gray-400">Ostatnie posty:</span>{' '}
                                  <span className="font-medium">{foundProfile.recentPostsCount || '❌ BRAK DANYCH'}</span>
                                </div>
                              </>
                            )}
                          </div>
                        </div>

                        {/* User Confirmation */}
                        <div className="pt-2 border-t border-blue-200 dark:border-blue-800">
                          <span className="text-gray-600 dark:text-gray-400">Potwierdzenie użytkownika (Q21):</span>{' '}
                          <span className={
                            userConfirmation === 'yes' ? 'text-green-600 font-semibold' :
                            userConfirmation === 'no' ? 'text-red-600 font-semibold' :
                            'text-gray-600 font-semibold'
                          }>
                            {userConfirmation === 'yes' ? '✓ Tak, to dobry profil' :
                             userConfirmation === 'no' ? '✗ Nie, to nieprawidłowy profil' :
                             userConfirmation === 'not_sure' ? '? Nie jestem pewien' :
                             'Brak odpowiedzi'}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* AI Analysis Section */}
                <div className="border-t pt-6 mt-6">
                  <div className="mb-4">
                    <h3 className="font-bold text-lg flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-primary" />
                      Analiza AI (wygenerowana dla użytkownika)
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      To, co użytkownik zobaczył w swoim raporcie
                    </p>
                  </div>

                  {(() => {
                    const aiAnalysis = selectedQuizSubmission?.ai_analysis;
                    
                    if (!aiAnalysis) {
                      return (
                        <div className="p-6 bg-muted/50 rounded-lg border-2 border-dashed text-center">
                          <p className="text-muted-foreground">
                            Brak zapisanej analizy AI dla tego użytkownika.
                          </p>
                          <p className="text-sm text-muted-foreground mt-2">
                            Analiza jest generowana automatycznie gdy użytkownik ogląda raport.
                          </p>
                        </div>
                      );
                    }

                    return (
                      <div className="space-y-4">
                        <div className="p-4 bg-gradient-to-br from-primary/10 to-primary/5 rounded-lg border border-primary/20">
                          <h4 className="font-bold text-lg mb-2">{aiAnalysis.title}</h4>
                          
                          {/* New 5-area structure */}
                          {aiAnalysis.areas ? (
                            <div className="space-y-4 mt-4">
                              {aiAnalysis.areas.map((area: any, idx: number) => (
                                <div key={idx} className="space-y-2">
                                  <h5 className="font-semibold text-primary text-sm">{area.name}</h5>
                                  <p className="text-sm leading-relaxed">{area.content}</p>
                                </div>
                              ))}
                            </div>
                          ) : (
                            /* Legacy single description format */
                            <p className="text-sm leading-relaxed whitespace-pre-wrap">
                              {aiAnalysis.description}
                            </p>
                          )}
                        </div>

                        {aiAnalysis.keyInsights && aiAnalysis.keyInsights.length > 0 && (
                          <div className="p-4 bg-muted rounded-lg border">
                            <h4 className="font-semibold mb-3">💡 Kluczowe obserwacje:</h4>
                            <ul className="space-y-2">
                              {aiAnalysis.keyInsights.map((insight: string, idx: number) => (
                                <li key={idx} className="text-sm flex gap-2">
                                  <span className="text-primary font-bold">•</span>
                                  <span>{insight}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="max-w-[95vw] md:max-w-md p-4 md:p-6">
          <DialogHeader>
            <DialogTitle>Zmień hasło</DialogTitle>
            <DialogDescription>
              Wprowadź nowe hasło do konta administratora
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">Nowe hasło</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Minimum 6 znaków"
                required
                minLength={6}
                maxLength={72}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Potwierdź hasło</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Powtórz nowe hasło"
                required
                minLength={6}
                maxLength={72}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setShowPasswordDialog(false);
                  setNewPassword("");
                  setConfirmPassword("");
                }}
              >
                Anuluj
              </Button>
              <Button type="submit" disabled={changingPassword}>
                {changingPassword && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Zmień hasło
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Competitor AI Test Dialog */}
      <Dialog open={showCompetitorTestDialog} onOpenChange={(open) => {
        setShowCompetitorTestDialog(open);
        if (!open) resetCompetitorTest();
      }}>
        <DialogContent className="max-w-[95vw] md:max-w-4xl max-h-[85vh] overflow-y-auto p-4 md:p-6">
          <DialogHeader>
            <DialogTitle>Competitor AI Test</DialogTitle>
            <DialogDescription>
              Przetestuj moduł wyszukiwania konkurencji dla dowolnej domeny (używa tego samego silnika co Quiz)
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleTestCompetitors} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="test-domain">Domena strony www</Label>
              <Input
                id="test-domain"
                type="text"
                placeholder="np. pragmatiq.pl lub https://www.mirox.pl"
                value={testDomain}
                onChange={(e) => setTestDomain(e.target.value)}
                disabled={testingCompetitors}
              />
              <p className="text-xs text-muted-foreground">
                Wpisz domenę lub pełny URL - system automatycznie znormalizuje adres
              </p>
            </div>

            <div className="flex items-center space-x-2 p-3 border rounded-lg bg-muted/30">
              <input
                type="checkbox"
                id="force-refresh"
                checked={forceRefresh}
                onChange={(e) => setForceRefresh(e.target.checked)}
                disabled={testingCompetitors}
                className="h-4 w-4 rounded border-gray-300"
              />
              <div className="flex-1">
                <Label htmlFor="force-refresh" className="cursor-pointer font-medium">
                  Force Refresh (Pełna analiza AI)
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  {forceRefresh 
                    ? "⚠️ Analiza może trwać 3-5 minut. Zostanie wykonana pełna analiza AI bez użycia cache."
                    : "💡 Dla trudnych domen najpierw spróbuj bez Force Refresh (używa cache, szybki wynik). Zaznacz tylko gdy potrzebujesz pełnej analizy."}
                </p>
              </div>
            </div>

            {testingCompetitors && (
              <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
                <div className="flex items-center gap-3">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <div className="flex-1">
                    <p className="font-medium">
                      {testStage === 'analyzing' && 'Analizuję stronę firmową...'}
                      {testStage === 'searching' && 'Wyszukuję konkurentów...'}
                      {testStage === 'verifying' && 'Weryfikuję wyniki...'}
                      {testStage === 'complete' && 'Gotowe!'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {longTimeouts 
                        ? `🔧 Diagnostic run with extended timeouts - Postęp: ${testProgress}%`
                        : forceRefresh 
                          ? `Postęp: ${testProgress}% - Analiza może trwać do 5 minut`
                          : `Postęp: ${testProgress}%`}
                    </p>
                  </div>
                </div>
                <div className="w-full bg-secondary rounded-full h-2 overflow-hidden">
                  <div 
                    className="bg-primary h-full transition-all duration-500"
                    style={{ width: `${testProgress}%` }}
                  />
                </div>
                {forceRefresh && testProgress >= 90 && (
                  <p className="text-xs text-muted-foreground text-center">
                    ⏳ Trwa finalizacja analizy AI - to może zająć jeszcze kilka minut...
                  </p>
                )}
              </div>
            )}

            {testResult && !testingCompetitors && (
              <div className="space-y-4">
                {testResult.success === false && testResult.error && (
                  <div className="p-4 border border-destructive rounded-lg bg-destructive/10">
                    <p className="text-sm font-semibold text-destructive mb-1">❌ Error:</p>
                    <p className="text-sm text-muted-foreground">{testResult.error}</p>
                    {longTimeouts && (
                      <p className="text-xs text-muted-foreground mt-2 italic">
                        This was a diagnostic run with extended timeouts. Check edge function logs for details.
                      </p>
                    )}
                  </div>
                )}
                <div className="p-4 border rounded-lg bg-muted/50 space-y-2">
                  <h3 className="font-semibold">Wynik analizy:</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-muted-foreground">Domena:</span>
                      <span className="ml-2 font-medium">{testResult.domain || normalizeDomain(testDomain)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Confidence:</span>
                      <Badge variant={
                        testResult.confidence === 'high' ? 'default' :
                        testResult.confidence === 'medium' ? 'secondary' :
                        testResult.confidence === 'low' ? 'outline' : 'destructive'
                      } className="ml-2">
                        {testResult.confidence || 'none'}
                      </Badge>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Konkurenci znalezieni:</span>
                      <span className="ml-2 font-medium">{testResult.competitors?.length || 0}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Status:</span>
                      <span className="ml-2 font-medium">
                        {testResult.competitors?.length >= 2 ? '✅ OK' : '⚠️ Za mało'}
                      </span>
                    </div>
                  </div>

                  {testResult.company_name && (
                    <div className="pt-2 border-t">
                      <span className="text-muted-foreground">Firma:</span>
                      <span className="ml-2 font-medium">{testResult.company_name}</span>
                    </div>
                  )}

                  {testResult.industry && (
                    <div className="grid grid-cols-2 gap-2 text-sm pt-2">
                      <div>
                        <span className="text-muted-foreground">Branża:</span>
                        <span className="ml-2">{testResult.industry}</span>
                      </div>
                      {testResult.sub_industry && (
                        <div>
                          <span className="text-muted-foreground">Sub-branża:</span>
                          <span className="ml-2">{testResult.sub_industry}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {testResult.competitors && testResult.competitors.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-semibold">Lista konkurentów:</h4>
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {testResult.competitors.map((competitor: any, idx: number) => (
                        <div key={idx} className="p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                          <div className="flex justify-between items-start gap-2">
                            <div className="flex-1">
                              <p className="font-medium">{competitor.company_name || competitor.domain || 'Unknown Company'}</p>
                              {competitor.domain && (
                                <a 
                                  href={`https://${competitor.domain}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-primary hover:underline"
                                >
                                  {competitor.domain}
                                </a>
                              )}
                              {competitor.description && (
                                <p className="text-xs text-muted-foreground mt-1">{competitor.description}</p>
                              )}
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              <Badge variant="outline" className="text-xs">
                                Score: {competitor.score || 'N/A'}
                              </Badge>
                              {competitor.competitive_level && (
                                <Badge variant={
                                  competitor.competitive_level === 'direct' ? 'default' : 'secondary'
                                } className="text-xs">
                                  {competitor.competitive_level}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {testResult.error && (
                  <div className="p-4 border border-destructive rounded-lg bg-destructive/10">
                    <p className="text-sm text-destructive font-medium">Error:</p>
                    <p className="text-xs text-destructive/80 mt-1">{testResult.error}</p>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-2 pt-4">
              <Button 
                type="submit" 
                disabled={testingCompetitors || !testDomain.trim()}
                className="flex-1"
              >
                {testingCompetitors ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Testuję...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-4 w-4" />
                    Uruchom test
                  </>
                )}
              </Button>
              
              {testResult && !testingCompetitors && (
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={resetCompetitorTest}
                >
                  Nowy test
                </Button>
              )}
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* API Check Dialog */}
      <Dialog open={showApiCheckDialog} onOpenChange={setShowApiCheckDialog}>
        <DialogContent className="max-w-[95vw] md:max-w-3xl max-h-[85vh] overflow-y-auto p-4 md:p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              Status połączeń API
            </DialogTitle>
            <DialogDescription>
              Test połączeń ze wszystkimi używanymi API
            </DialogDescription>
          </DialogHeader>
          
          {apiCheckResults && (
            <div className="space-y-6">
              {/* Summary */}
              <Card>
                <CardContent className="pt-6">
                  <div className="grid grid-cols-4 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold">{apiCheckResults.summary.total}</p>
                      <p className="text-xs text-muted-foreground">Total API</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-500">{apiCheckResults.summary.success}</p>
                      <p className="text-xs text-muted-foreground">Sukces</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-red-500">{apiCheckResults.summary.error}</p>
                      <p className="text-xs text-muted-foreground">Błędy</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-yellow-500">{apiCheckResults.summary.warning}</p>
                      <p className="text-xs text-muted-foreground">Ostrzeżenia</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Results List */}
              <div className="space-y-3">
                {apiCheckResults.results.map((result: any, index: number) => {
                  const StatusIcon = result.status === 'success' ? CheckCircle2 : 
                                    result.status === 'error' ? XCircle :
                                    result.status === 'warning' ? AlertTriangle : 
                                    AlertTriangle;
                  
                  const statusColor = result.status === 'success' ? 'text-green-500' : 
                                     result.status === 'error' ? 'text-red-500' :
                                     result.status === 'warning' ? 'text-yellow-500' : 
                                     'text-gray-500';
                  
                  const bgColor = result.status === 'success' ? 'bg-green-500/10' : 
                                 result.status === 'error' ? 'bg-red-500/10' :
                                 result.status === 'warning' ? 'bg-yellow-500/10' : 
                                 'bg-gray-500/10';

                  return (
                    <Card key={index} className={bgColor}>
                      <CardContent className="pt-6">
                        <div className="flex items-start gap-3">
                          <StatusIcon className={`h-6 w-6 ${statusColor} flex-shrink-0 mt-1`} />
                          <div className="flex-1 space-y-2">
                            <div className="flex items-center justify-between">
                              <h3 className="font-semibold">{result.name}</h3>
                              {result.responseTime && (
                                <Badge variant="outline" className="text-xs">
                                  <Clock className="h-3 w-3 mr-1" />
                                  {result.responseTime}ms
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm font-medium">{result.message}</p>
                            {result.details && (
                              <p className="text-xs text-muted-foreground">{result.details}</p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Timestamp */}
              <p className="text-xs text-muted-foreground text-center">
                Testowano: {new Date(apiCheckResults.testedAt).toLocaleString('pl-PL')}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Salesflare Structure Dialog */}
      <Dialog open={showSalesflareDialog} onOpenChange={setShowSalesflareDialog}>
        <DialogContent className="max-w-[95vw] md:max-w-4xl max-h-[85vh] overflow-y-auto p-4 md:p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5" />
              Salesflare Pipeline Structure
            </DialogTitle>
            <DialogDescription>
              Your Salesflare account configuration
            </DialogDescription>
          </DialogHeader>
          
          {salesflareTestResult && (
            <div className="space-y-6">
              {/* Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold">{salesflareTestResult.summary?.totalPipelines || 0}</p>
                      <p className="text-xs text-muted-foreground">Pipelines</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{salesflareTestResult.summary?.totalStages || 0}</p>
                      <p className="text-xs text-muted-foreground">Stages</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{salesflareTestResult.summary?.totalCustomFields || 0}</p>
                      <p className="text-xs text-muted-foreground">Custom Fields</p>
                    </div>
                  </div>
                  {salesflareTestResult.summary?.socialSellingPipeline && (
                    <div className="mt-4 p-3 bg-green-500/10 rounded-lg">
                      <p className="text-sm font-semibold text-green-600">
                        ✅ Found Social Selling Pipeline: {salesflareTestResult.summary.socialSellingPipeline.name}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        ID: {salesflareTestResult.summary.socialSellingPipeline.id}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Pipelines and Stages */}
              {salesflareTestResult.pipelines && salesflareTestResult.pipelines.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Pipelines & Stages</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {salesflareTestResult.pipelines.map((pipeline: any, idx: number) => (
                      <div key={idx} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-semibold">{pipeline.name}</h4>
                          <Badge>ID: {pipeline.id}</Badge>
                        </div>
                        {pipeline.stages && pipeline.stages.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-sm text-muted-foreground mb-2">Stages ({pipeline.stages.length}):</p>
                            <div className="grid gap-2">
                              {pipeline.stages.map((stage: any, sIdx: number) => (
                                <div key={sIdx} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs font-mono text-muted-foreground">#{stage.order}</span>
                                    <span className="text-sm font-medium">{stage.name}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {stage.probability !== undefined && (
                                      <Badge variant="outline" className="text-xs">
                                        {stage.probability}%
                                      </Badge>
                                    )}
                                    <Badge variant="secondary" className="text-xs">
                                      ID: {stage.id}
                                    </Badge>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Custom Fields */}
              {salesflareTestResult.customFields && salesflareTestResult.customFields.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Custom Fields</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-2">
                      {salesflareTestResult.customFields.map((field: any, idx: number) => {
                        const fieldType = typeof field.type === 'object'
                          ? (field.type.displayName || field.type.name || field.type.type || JSON.stringify(field.type))
                          : String(field.type);
                        return (
                          <div key={idx} className="flex items-center justify-between p-3 border rounded-lg">
                            <div className="flex-1">
                              <p className="font-medium text-sm">{field.name}</p>
                              <p className="text-xs text-muted-foreground">Key: {field.key}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline">{fieldType}</Badge>
                              <Badge variant="secondary" className="text-xs">
                                ID: {field.id}
                              </Badge>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Raw JSON (for debugging) */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Raw Data (for development)</CardTitle>
                </CardHeader>
                <CardContent>
                  <pre className="text-xs bg-muted p-4 rounded overflow-x-auto max-h-96">
                    {JSON.stringify(salesflareTestResult, null, 2)}
                  </pre>
                </CardContent>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Audit Progress Dialog */}
      {selectedAuditId && (
        <AuditProgressDialog
          submissionId={selectedAuditId}
          open={progressDialogOpen}
          onClose={() => {
            setProgressDialogOpen(false);
            setSelectedAuditId(null);
            fetchSocialSellingSubmissions();
          }}
        />
      )}

      {/* Quiz Enriched Data Viewer */}
      <QuizEnrichedDataViewer
        open={showQuizEnrichedDialog}
        onOpenChange={setShowQuizEnrichedDialog}
        enrichedData={selectedQuizSubmission?.enriched_data || null}
        domain={selectedQuizSubmission?.domain_input || ''}
        quizAnswers={selectedQuizSubmission?.quiz_answers}
      />

      <LeadEnrichedDataViewer
        open={showLeadEnrichedDialog}
        onOpenChange={setShowLeadEnrichedDialog}
        enrichedData={selectedSubmission?.enriched_data || null}
        domain={selectedSubmission?.enriched_data?.domain || selectedSubmission?.enriched_data?.company_name || selectedSubmission?.email || ''}
      />

      {/* Notes Edit Modal */}
      <Dialog open={notesModal.isOpen} onOpenChange={(open) => !open && setNotesModal({ isOpen: false, type: null, id: null, notes: '' })}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <StickyNote className="h-4 w-4" />
              Edytuj notatki
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Textarea
              placeholder="Wpisz notatki..."
              value={notesModal.notes}
              onChange={(e) => setNotesModal(prev => ({ ...prev, notes: e.target.value }))}
              className="min-h-[150px] text-sm"
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setNotesModal({ isOpen: false, type: null, id: null, notes: '' })}
              >
                Anuluj
              </Button>
              <Button
                onClick={async () => {
                  if (notesModal.type === 'quiz' && notesModal.id) {
                    await handleUpdateQuizNotes(notesModal.id, notesModal.notes);
                    setQuizSubmissions(prev =>
                      prev.map(q => q.id === notesModal.id ? { ...q, notes: notesModal.notes } : q)
                    );
                  } else if (notesModal.type === 'lead' && notesModal.id) {
                    await handleUpdateLeadNotes(notesModal.id, notesModal.notes);
                    setSubmissions(prev =>
                      prev.map(l => l.id === notesModal.id ? { ...l, notes: notesModal.notes } : l)
                    );
                  }
                  setNotesModal({ isOpen: false, type: null, id: null, notes: '' });
                  toast({ title: "Zapisano", description: "Notatki zostały zaktualizowane" });
                }}
              >
                Zapisz
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Enrichment Manual Data Modal */}
      <Dialog 
        open={enrichmentModal.isOpen} 
        onOpenChange={(open) => !open && setEnrichmentModal(prev => ({ ...prev, isOpen: false }))}
      >
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              Wzbogacanie leada: {enrichmentModal.submission?.name}
            </DialogTitle>
            <DialogDescription>
              {enrichmentModal.submission?.email && isPrivateEmail(enrichmentModal.submission.email) && (
                <div className="text-amber-600 bg-amber-50 dark:bg-amber-950/30 dark:text-amber-400 p-3 rounded-md mt-2 text-sm">
                  ⚠️ Email prywatny ({enrichmentModal.submission.email}) - automatyczne wzbogacanie może nie znaleźć danych firmowych. Podaj dane jeśli je znasz.
                </div>
              )}
              <p className="mt-3 text-muted-foreground">
                Opcjonalnie podaj dane firmowe z rozmowy telefonicznej. Pozostaw puste pola aby enrichment działał automatycznie.
              </p>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="manualDomain">Domena firmowa</Label>
              <Input 
                id="manualDomain"
                placeholder="np. highai.pl" 
                value={enrichmentModal.manualDomain}
                onChange={(e) => setEnrichmentModal(prev => ({ ...prev, manualDomain: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">
                Domena strony firmowej (bez http://)
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="manualKrs">Numer KRS</Label>
              <Input 
                id="manualKrs"
                placeholder="np. 0000123456" 
                value={enrichmentModal.manualKrs}
                onChange={(e) => setEnrichmentModal(prev => ({ ...prev, manualKrs: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">
                10-cyfrowy numer KRS (opcjonalne)
              </p>
            </div>
          </div>
          
          <div className="flex justify-end gap-2">
            <Button 
              variant="outline" 
              onClick={() => setEnrichmentModal({ isOpen: false, submission: null, manualDomain: '', manualKrs: '' })}
            >
              Anuluj
            </Button>
            <Button onClick={handleEnrichWithManualData}>
              <Sparkles className="h-4 w-4 mr-2" />
              Rozpocznij wzbogacanie
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;